import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Search, Filter, Crosshair, Mail, Plus, Trash2,
    ChevronLeft, ChevronRight, Send, X, Loader2, RefreshCw,
    ExternalLink, Globe, MapPin, Users, Star, CalendarPlus,
    ChevronDown, ChevronUp, Pencil, Phone, Briefcase, Check,
    AlertTriangle, Clock, Zap, LayoutGrid, List, ArrowRight,
    IndianRupee, TrendingUp, Calendar, MessageSquare,
    Play, StopCircle, Layers, CheckSquare, Square,
    SlidersHorizontal, Building2, Banknote,
    Copy, Download, Upload, FileJson, FileSpreadsheet, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea }   from '@/components/ui/textarea';
import { Separator }  from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// ─── Pipeline stage config ────────────────────────────────────────────────────
// Canonical pipeline stages (new schema names).
// Legacy aliases (demo_scheduled, converted, not_interested) are mapped below.
const PIPELINE = [
    { key: 'new',          label: 'New',          color: '#3b82f6', bg: 'bg-blue-500' },
    { key: 'researching',  label: 'Researching',  color: '#64748b', bg: 'bg-slate-500' },
    { key: 'contacted',    label: 'Contacted',    color: '#f59e0b', bg: 'bg-amber-500' },
    { key: 'engaged',      label: 'Engaged',      color: '#8b5cf6', bg: 'bg-violet-500' },
    { key: 'demo_booked',  label: 'Demo Booked',  color: '#06b6d4', bg: 'bg-cyan-500' },
    { key: 'demo_done',    label: 'Demo Done',    color: '#ec4899', bg: 'bg-pink-500' },
    { key: 'proposal_sent',label: 'Proposal',     color: '#f97316', bg: 'bg-orange-500' },
    { key: 'negotiating',  label: 'Negotiating',  color: '#eab308', bg: 'bg-yellow-500' },
    { key: 'won',          label: 'Won ✓',        color: '#22c55e', bg: 'bg-green-500' },
];
const DEAD_STAGES = {
    not_now:     { label: 'Not Now',     color: '#ef4444' },
    lost:        { label: 'Lost',        color: '#6b7280' },
    blacklisted: { label: 'Blacklisted', color: '#1e1e2e' },
};

// Legacy alias → canonical stage (for counts + display)
const STATUS_ALIAS = {
    demo_scheduled: 'demo_booked',
    converted:      'won',
    not_interested: 'not_now',
    replied:        'engaged',
};

// Full display map — covers canonical + all legacy aliases
const STATUS_MAP = {
    ...Object.fromEntries(PIPELINE.map(p => [p.key, p])),
    ...Object.fromEntries(Object.entries(DEAD_STAGES).map(([k, v]) => [k, { key: k, ...v, bg: 'bg-red-500' }])),
    // legacy aliases share display with their canonical counterpart
    demo_scheduled: { key: 'demo_scheduled', label: 'Demo Booked',  color: '#06b6d4', bg: 'bg-cyan-500' },
    converted:      { key: 'converted',      label: 'Won ✓',        color: '#22c55e', bg: 'bg-green-500' },
    not_interested: { key: 'not_interested',  label: 'Not Now',      color: '#ef4444', bg: 'bg-red-500' },
    replied:        { key: 'replied',         label: 'Engaged',      color: '#8b5cf6', bg: 'bg-violet-500' },
};

const URGENCY = {
    critical: { cls: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400',    icon: AlertTriangle, dot: 'bg-red-500' },
    high:     { cls: 'bg-orange-500/10 border-orange-500/30 text-orange-700 dark:text-orange-400', icon: Zap,           dot: 'bg-orange-500' },
    medium:   { cls: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400',   icon: Clock,         dot: 'bg-amber-500' },
    low:      { cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400', icon: Check,    dot: 'bg-emerald-500' },
    none:     { cls: 'bg-muted/50 border-border text-muted-foreground',                   icon: Check,         dot: 'bg-muted-foreground' },
};

const LOG_TYPE_ICONS = { email: Mail, call: Phone, linkedin: ExternalLink, demo: Calendar, meeting: Users, note: MessageSquare, whatsapp: MessageSquare };

const DELIVERY_STATUS_CONFIG = {
    sent:           { label: 'Sent',        cls: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
    delivered:      { label: 'Delivered',   cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
    opened:         { label: 'Opened',      cls: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
    clicked:        { label: 'Clicked',     cls: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' },
    bounced_soft:   { label: 'Soft Bounce', cls: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
    bounced_hard:   { label: 'Hard Bounce', cls: 'bg-red-500/10 text-red-600 border-red-500/20' },
    complained:     { label: 'Spam Report', cls: 'bg-red-500/10 text-red-700 border-red-500/30' },
    reply_received: { label: 'Replied ✓',   cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' },
    pending:        { label: null, cls: '' }, // don't show badge for pending
};
const TIER_COLOR = { 1: 'text-amber-500', 2: 'text-blue-500', 3: 'text-muted-foreground' };

const ALL_STATUSES = PIPELINE.map(p => p.key);

// ─── Small reusable UI atoms ──────────────────────────────────────────────────
function StatusBadge({ status }) {
    const s = STATUS_MAP[status] || { label: status, color: '#888' };
    return (
        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold"
            style={{ color: s.color, borderColor: `${s.color}33`, background: `${s.color}14` }}>
            {s.label}
        </span>
    );
}

function TierStars({ tier }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3].map(i => (
                <Star key={i} className={cn('h-3 w-3', i <= tier ? TIER_COLOR[tier] : 'text-muted-foreground/20')}
                    fill={i <= tier ? 'currentColor' : 'none'} />
            ))}
        </div>
    );
}

function SuggestionCard({ suggestion, onCtaClick }) {
    if (!suggestion || suggestion.urgency === 'none') return null;
    const u = URGENCY[suggestion.urgency] || URGENCY.none;
    const Icon = u.icon;
    return (
        <div className={cn('rounded-lg border p-3 space-y-2', u.cls)}>
            <div className="flex items-start gap-2">
                <span className={cn('mt-0.5 h-2 w-2 shrink-0 rounded-full', u.dot)} />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wide mb-0.5">
                        {suggestion.urgency === 'critical' ? '⚡ Act Now' : suggestion.urgency === 'high' ? '🔥 High Priority' : suggestion.urgency === 'medium' ? '📋 Do This Next' : '✓ On Track'}
                    </p>
                    <p className="text-[13px] leading-relaxed">{suggestion.text}</p>
                </div>
            </div>
            {suggestion.cta && (
                <button type="button" onClick={() => onCtaClick?.(suggestion.ctaAction)}
                    className={cn('ml-4 text-xs font-semibold underline underline-offset-2 hover:no-underline')}>
                    → {suggestion.cta}
                </button>
            )}
        </div>
    );
}

// ─── Pipeline Funnel bar ──────────────────────────────────────────────────────
function PipelineFunnel({ stats, activeStage, onStageClick }) {
    const byStatus = stats?.byStatus || {};
    const total    = stats?.total || 1;
    // Merge legacy alias counts into canonical key counts
    const merged = { ...byStatus };
    Object.entries(STATUS_ALIAS).forEach(([alias, canonical]) => {
        merged[canonical] = (merged[canonical] || 0) + (merged[alias] || 0);
    });
    return (
        <div className="overflow-x-auto pb-1">
            <div className="flex items-stretch gap-0 min-w-max">
                {PIPELINE.map((stage, i) => {
                    const count = merged[stage.key] || 0;
                    const pct   = Math.round((count / total) * 100);
                    const prev  = i > 0 ? (byStatus[PIPELINE[i - 1].key] || 0) : null;
                    const conv  = prev && prev > 0 ? Math.round((count / prev) * 100) : null;
                    const isActive = activeStage === stage.key;
                    return (
                        <div key={stage.key} className="flex items-stretch">
                            <button type="button" onClick={() => onStageClick(stage.key)}
                                className={cn('group flex flex-col items-center justify-center min-w-[100px] px-3 py-3 rounded-none transition-all border-y border-r first:border-l first:rounded-l-lg last:rounded-r-lg',
                                    isActive ? 'bg-muted border-primary/50' : 'bg-card border-border hover:bg-muted/50'
                                )}>
                                <div className={cn('h-1.5 w-full rounded-full mb-2 transition-all', isActive ? stage.bg : 'bg-muted-foreground/20')}
                                    style={{ width: `${Math.max(10, pct)}%`, minWidth: '100%' }} />
                                <span className={cn('text-xl font-black tabular-nums', isActive ? 'text-foreground' : 'text-muted-foreground')}>
                                    {count}
                                </span>
                                <span className="text-[10px] font-semibold text-center leading-tight mt-0.5"
                                    style={{ color: isActive ? stage.color : undefined }}>{stage.label}</span>
                                {conv !== null && (
                                    <span className="text-[9px] text-muted-foreground mt-0.5">{conv}% conv.</span>
                                )}
                            </button>
                            {i < PIPELINE.length - 1 && (
                                <ArrowRight className="h-4 w-4 self-center text-muted-foreground/30 mx-0.5 shrink-0" />
                            )}
                        </div>
                    );
                })}
                {/* dead stages */}
                <div className="flex items-center gap-1 ml-3 pl-3 border-l border-border">
                    {Object.entries(DEAD_STAGES).map(([key, { label, color }]) => (
                        <button key={key} type="button" onClick={() => onStageClick(key)}
                            className={cn('flex flex-col items-center justify-center min-w-[80px] px-2 py-3 rounded-lg border transition-all',
                                activeStage === key ? 'bg-muted border-primary/50' : 'bg-card border-border hover:bg-muted/50')}>
                            <span className="text-xl font-black tabular-nums text-muted-foreground">{merged[key] || 0}</span>
                            <span className="text-[10px] font-semibold text-center leading-tight mt-0.5" style={{ color }}>{label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── Kanban Card ─────────────────────────────────────────────────────────────
function KanbanCard({ lead, onSelect }) {
    const overdue     = lead.nextAction?.date && new Date(lead.nextAction.date) < new Date();
    const seqActive   = lead.sequence?.active;
    const logCount    = (lead.contactLogs?.length || 0) + (lead.outreachHistory?.length || 0);
    const lastEmailLog = [...(lead.contactLogs || [])].reverse()
        .find(l => l.type === 'email' && l.deliveryStatus && l.deliveryStatus !== 'pending');

    return (
        <button type="button" onClick={() => onSelect(lead)}
            className={cn(
                'w-full text-left rounded-xl border bg-card p-3.5 hover:shadow-md transition-all group space-y-2.5',
                overdue ? 'border-red-500/30 hover:border-red-500/50' : 'border-border hover:border-primary/40 hover:bg-card/80'
            )}>
            {/* Name + tier */}
            <div className="flex items-start justify-between gap-1.5">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight truncate">{lead.name}</p>
                    <a href={`https://${lead.domain}`} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-0.5 mt-0.5 w-fit">
                        {lead.domain} <ExternalLink className="h-2 w-2 ml-0.5" />
                    </a>
                </div>
                <TierStars tier={lead.tier} />
            </div>

            {/* Contact */}
            {(lead.contactPerson || lead.contactPersonRole) && (
                <div className="flex items-center gap-1.5">
                    <Users className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                    <p className="text-[11px] text-muted-foreground truncate">
                        {lead.contactPerson}{lead.contactPersonRole ? ` · ${lead.contactPersonRole}` : ''}
                    </p>
                </div>
            )}

            {/* Industry + size chips */}
            {(lead.industry || lead.size) && (
                <div className="flex items-center gap-1 flex-wrap">
                    {lead.industry && (
                        <span className="inline-flex items-center rounded-full bg-muted/80 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground border border-border/50">
                            {lead.industry}
                        </span>
                    )}
                    {lead.size && (
                        <span className="inline-flex items-center rounded-full bg-muted/80 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground border border-border/50">
                            {lead.size}
                        </span>
                    )}
                </div>
            )}

            {/* Deal value */}
            {lead.dealValue > 0 && (
                <div className="flex items-center gap-0.5">
                    <IndianRupee className="h-3 w-3 text-emerald-500" />
                    <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                        {lead.dealValue.toLocaleString('en-IN')}/mo
                    </span>
                </div>
            )}

            {/* Footer row */}
            <div className="flex items-center justify-between gap-1 pt-2 border-t border-border/50">
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Overdue / next action */}
                    {lead.nextAction?.date && (
                        <div className={cn('flex items-center gap-0.5 text-[10px] font-medium',
                            overdue ? 'text-red-500' : 'text-amber-600 dark:text-amber-400')}>
                            {overdue ? <AlertTriangle className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
                            {new Date(lead.nextAction.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </div>
                    )}

                    {/* Active drip indicator + next step badge */}
                    {seqActive && (() => {
                        const stepLabels = ['Cold','* FU 1','* FU 2','* FU 3'];
                        const s = lead.sequence;
                        const today = new Date(); today.setHours(0,0,0,0);
                        const isOverdue = s.nextSendAt && new Date(s.nextSendAt) < today;
                        const label = stepLabels[s.step] || `Step ${(s.step || 0) + 1}`;
                        const date  = s.nextSendAt ? new Date(s.nextSendAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
                        return (
                            <span className={cn('inline-flex items-center gap-0.5 rounded px-1 py-0 text-[9px] font-bold',
                                isOverdue ? 'bg-red-500/15 text-red-600' : 'bg-violet-500/15 text-violet-600')}>
                                {isOverdue ? '⚠' : '▶'} {label}{date ? ` ${date}` : ''}
                            </span>
                        );
                    })()}

                    {/* Last email delivery badge */}
                    {lastEmailLog && DELIVERY_STATUS_CONFIG[lastEmailLog.deliveryStatus]?.label && (
                        <span className={cn('inline-flex items-center rounded-full border px-1.5 py-0 text-[9px] font-semibold',
                            DELIVERY_STATUS_CONFIG[lastEmailLog.deliveryStatus].cls)}>
                            {DELIVERY_STATUS_CONFIG[lastEmailLog.deliveryStatus].label}
                        </span>
                    )}
                </div>

                {logCount > 0 && (
                    <span className="text-[9px] text-muted-foreground/50 tabular-nums shrink-0">
                        {logCount} log{logCount !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
        </button>
    );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────
function KanbanColumn({ stage, leads, onSelect }) {
    const totalDeal = leads.reduce((s, l) => s + (l.dealValue || 0), 0);

    return (
        <div className="shrink-0 w-[270px] flex flex-col rounded-xl border border-border/60 bg-muted/20 overflow-hidden">
            {/* Column header */}
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border/60"
                style={{ background: `${stage.color}10` }}>
                <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
                    <span className="text-xs font-bold tracking-wide" style={{ color: stage.color }}>{stage.label}</span>
                </div>
                <div className="flex items-center gap-2">
                    {totalDeal > 0 && (
                        <span className="text-[9px] font-semibold text-emerald-600 dark:text-emerald-400 hidden sm:inline">
                            ₹{(totalDeal / 1000).toFixed(0)}k
                        </span>
                    )}
                    <span className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-background/80 border border-border/60 text-[10px] font-bold text-foreground px-1.5">
                        {leads.length}
                    </span>
                </div>
            </div>

            {/* Cards scroll area */}
            <div className="flex flex-col gap-2 p-2.5 overflow-y-auto max-h-[calc(100vh-320px)] min-h-[120px]">
                {leads.map(lead => (
                    <KanbanCard key={lead._id} lead={lead} onSelect={onSelect} />
                ))}
                {!leads.length && (
                    <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-center">
                        <div className="h-8 w-8 rounded-full border-2 border-dashed border-border/60 flex items-center justify-center">
                            <Plus className="h-3.5 w-3.5 text-muted-foreground/30" />
                        </div>
                        <p className="text-[10px] text-muted-foreground/40 font-medium">No leads here</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Kanban Board ─────────────────────────────────────────────────────────────
function KanbanBoard({ leads, onSelect }) {
    const grouped = {};
    PIPELINE.forEach(s => { grouped[s.key] = []; });
    leads.forEach(l => {
        const key = STATUS_ALIAS[l.status] || l.status;
        if (grouped[key]) grouped[key].push(l);
    });

    return (
        <div className="flex gap-3 overflow-x-auto pb-4 items-start">
            {PIPELINE.map(stage => (
                <KanbanColumn key={stage.key} stage={stage} leads={grouped[stage.key]} onSelect={onSelect} />
            ))}
        </div>
    );
}

// ─── Stable text-field helper (MUST be top-level — never inside another component)
// Defining components inside a render function causes React to unmount+remount
// on every keystroke, killing input focus.
function FormField({ label, value, onChange, type = 'text', placeholder }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            <Input type={type} value={value} onChange={onChange} placeholder={placeholder} className="text-sm h-9" />
        </div>
    );
}

// ─── Shared select style ──────────────────────────────────────────────────────
const SEL = 'flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const SECTION = 'text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1';

// Blank contact template
const blankContact = () => ({
    firstName: '', lastName: '', title: '', email: '', mobile: '',
    persona: '', seniority: '', isPrimary: false, isDecisionMaker: false,
    linkedinUrl: '', notes: '',
});

// ─── Edit Lead Modal ──────────────────────────────────────────────────────────
function EditLeadModal({ lead, onClose, onSaved }) {
    const initContacts = (lead.contacts && lead.contacts.length)
        ? lead.contacts.map(c => ({ ...blankContact(), ...c }))
        : (lead.contactPerson || lead.email)
            ? [{ ...blankContact(), firstName: lead.contactPerson?.split(' ')[0] || '', lastName: lead.contactPerson?.split(' ').slice(1).join(' ') || '', title: lead.contactPersonRole || '', email: lead.email || '', mobile: lead.phone || '', isPrimary: true }]
            : [];

    const [form, setForm] = useState({
        name: lead.name || '', domain: lead.domain || '',
        website: lead.website || '', city: lead.city || '', state: lead.state || '', country: lead.country || 'India',
        industry: lead.industry || '',
        size: lead.size || '', fundingStage: lead.fundingStage || '', openRolesCount: lead.openRolesCount ?? '',
        emailFormat: lead.emailFormat || '', careersEmail: lead.careersEmail || '',
        linkedinUrl: lead.linkedinUrl || '', apolloAccountId: lead.apolloAccountId || '',
        tier: lead.tier || 2, source: lead.source || 'manual_research',
        why: lead.why || '', notes: lead.notes || '', lostReason: lead.lostReason || '',
        dealValue: lead.dealValue || '', expectedCloseDate: lead.expectedCloseDate ? new Date(lead.expectedCloseDate).toISOString().split('T')[0] : '',
    });
    const [contacts, setContacts] = useState(initContacts);
    const [saving, setSaving] = useState(false);

    const set  = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const ff   = (k)    => ({ value: form[k] ?? '', onChange: (e) => set(k, e.target.value) });
    const setC = (i, k, v) => setContacts(prev => prev.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

    const makePrimary = (i) => setContacts(prev => prev.map((c, idx) => ({ ...c, isPrimary: idx === i })));
    const removeContact = (i) => {
        const next = contacts.filter((_, idx) => idx !== i);
        if (next.length && !next.some(c => c.isPrimary)) next[0].isPrimary = true;
        setContacts(next);
    };

    const handleSave = async () => {
        if (!form.name || !form.domain) { toast.error('Name and domain are required.'); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                tier: Number(form.tier),
                openRolesCount: form.openRolesCount !== '' ? Number(form.openRolesCount) : 0,
                dealValue: form.dealValue ? Number(form.dealValue) : 0,
                contacts,
            };
            if (!payload.expectedCloseDate) delete payload.expectedCloseDate;
            const r = await apiClient.updateProspectLead(lead._id, payload);
            toast.success('Lead updated.');
            onSaved?.(r.data);
            onClose();
        } catch (e) { toast.error(e.message || 'Failed to update.'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4 text-primary" /> Edit Lead — {lead.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">

                    {/* ── Company ── */}
                    <p className={SECTION}>Company</p>
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Company Name *" {...ff('name')} />
                        <FormField label="Domain *" {...ff('domain')} placeholder="acme.com" />
                        <FormField label="Website" {...ff('website')} placeholder="https://acme.com" />
                        <FormField label="LinkedIn URL" {...ff('linkedinUrl')} placeholder="https://linkedin.com/company/…" />
                        <FormField label="Industry" {...ff('industry')} placeholder="SaaS, Fintech, HRTech…" />
                        <div className="space-y-1">
                            <Label className="text-xs">Company Size</Label>
                            <select value={form.size} onChange={e => set('size', e.target.value)} className={SEL}>
                                {['','1-10','11-20','21-50','51-100','101-200','201-500','501-1000','1000+'].map(s => <option key={s} value={s}>{s || 'Select…'}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Funding Stage</Label>
                            <select value={form.fundingStage} onChange={e => set('fundingStage', e.target.value)} className={SEL}>
                                {['','Bootstrapped','Pre-Seed','Seed','Series A','Series B','Series C','Series D+','Public','Unknown'].map(s => <option key={s} value={s}>{s || 'Select…'}</option>)}
                            </select>
                        </div>
                        <FormField label="Open Roles Count" {...ff('openRolesCount')} type="number" placeholder="8" />
                        <FormField label="Email Format" {...ff('emailFormat')} placeholder="{first}.{last}@acme.com" />
                        <FormField label="Careers / HR Email" {...ff('careersEmail')} type="email" placeholder="careers@acme.com" />
                        <FormField label="Apollo Account ID" {...ff('apolloAccountId')} placeholder="Optional" />
                    </div>

                    {/* ── Location ── */}
                    <p className={SECTION}>Location</p>
                    <div className="grid grid-cols-3 gap-3">
                        <FormField label="City" {...ff('city')} />
                        <FormField label="State" {...ff('state')} />
                        <FormField label="Country" {...ff('country')} />
                    </div>

                    {/* ── Contacts ── */}
                    <div className="flex items-center justify-between mt-1">
                        <p className={SECTION}>Contacts</p>
                        <button type="button" onClick={() => setContacts(prev => [...prev, blankContact()])}
                            className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                            <Plus className="h-3 w-3" /> Add Contact
                        </button>
                    </div>
                    {contacts.length === 0 && (
                        <p className="text-xs text-muted-foreground">No contacts yet — add at least one person to reach out to.</p>
                    )}
                    {contacts.map((c, i) => (
                        <div key={i} className={cn('rounded-lg border p-3 space-y-2', c.isPrimary ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/20')}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    {c.isPrimary
                                        ? <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Primary</span>
                                        : <button type="button" onClick={() => makePrimary(i)} className="text-[10px] text-muted-foreground hover:text-primary font-semibold">Set Primary</button>}
                                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer">
                                        <input type="checkbox" checked={c.isDecisionMaker} onChange={e => setC(i, 'isDecisionMaker', e.target.checked)} className="h-3 w-3" />
                                        Decision Maker
                                    </label>
                                </div>
                                <button type="button" onClick={() => removeContact(i)} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <FormField label="First Name" value={c.firstName} onChange={e => setC(i, 'firstName', e.target.value)} />
                                <FormField label="Last Name"  value={c.lastName}  onChange={e => setC(i, 'lastName',  e.target.value)} />
                                <FormField label="Title / Role" value={c.title} onChange={e => setC(i, 'title', e.target.value)} placeholder="HR Manager, Founder…" />
                                <FormField label="Email" value={c.email} onChange={e => setC(i, 'email', e.target.value)} type="email" />
                                <FormField label="Mobile" value={c.mobile} onChange={e => setC(i, 'mobile', e.target.value)} placeholder="+91 98765 43210" />
                                <FormField label="LinkedIn URL" value={c.linkedinUrl} onChange={e => setC(i, 'linkedinUrl', e.target.value)} />
                                <div className="space-y-1">
                                    <Label className="text-xs">Persona</Label>
                                    <select value={c.persona} onChange={e => setC(i, 'persona', e.target.value)} className={SEL}>
                                        {['','hr_manager','ta_head','hr_director','founder','ceo','coo','chief_people_officer','recruiter','other'].map(p => <option key={p} value={p}>{p || 'Select…'}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Seniority</Label>
                                    <select value={c.seniority} onChange={e => setC(i, 'seniority', e.target.value)} className={SEL}>
                                        {['','intern','junior','mid','senior','manager','director','vp','c_suite','founder','owner'].map(s => <option key={s} value={s}>{s || 'Select…'}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* ── Deal ── */}
                    <p className={SECTION}>Deal</p>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Tier</Label>
                            <select value={form.tier} onChange={e => set('tier', e.target.value)} className={SEL}>
                                <option value={1}>🔥 Tier 1 — Highest</option>
                                <option value={2}>⭐ Tier 2 — High</option>
                                <option value={3}>Tier 3 — Medium</option>
                                <option value={4}>Tier 4 — Low</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Expected Value (₹/mo)</Label>
                            <Input type="number" value={form.dealValue} onChange={e => set('dealValue', e.target.value)} placeholder="5000" className="text-sm h-9" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Expected Close Date</Label>
                            <input type="date" value={form.expectedCloseDate} onChange={e => set('expectedCloseDate', e.target.value)} className={SEL} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Source</Label>
                            <select value={form.source} onChange={e => set('source', e.target.value)} className={SEL}>
                                <option value="manual_research">Manual Research</option>
                                <option value="apollo">Apollo.io</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="referral">Referral</option>
                                <option value="inbound">Inbound</option>
                                <option value="event">Event</option>
                                <option value="product_hunt">Product Hunt</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <FormField label="Qualifier / Why This Lead" {...ff('why')} placeholder="Series B, 8 open roles, hiring spike…" />
                    </div>
                    <FormField label="Internal Notes" {...ff('notes')} placeholder="Anything else the team should know…" />

                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="gap-2">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            {saving ? 'Saving…' : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Log Contact Modal ────────────────────────────────────────────────────────
function LogContactModal({ lead, onClose, onSaved, prefillType }) {
    const [type, setType]           = useState(prefillType || 'note');
    const [direction, setDirection] = useState('outbound');
    const [subject, setSubject]     = useState('');
    const [body, setBody]           = useState('');
    const [outcome, setOutcome]     = useState('');
    const [nextDate, setNextDate]   = useState('');
    const [statusUpd, setStatusUpd] = useState('');
    const [saving, setSaving]       = useState(false);

    const handleSave = async () => {
        if (!body.trim()) { toast.error('Notes / body are required.'); return; }
        setSaving(true);
        try {
            const r = await apiClient.addProspectContactLog(lead._id, {
                type, direction, subject, body, outcome, nextFollowUpDate: nextDate || null,
                statusUpdate: statusUpd || undefined,
            });
            toast.success('Contact logged!');
            onSaved?.(r.data);
            onClose();
        } catch (e) { toast.error(e.message || 'Failed.'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Log Contact — {lead.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Type</Label>
                            <select value={type} onChange={e => setType(e.target.value)}
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                                {['email','call','linkedin','demo','meeting','note','whatsapp'].map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Direction</Label>
                            <select value={direction} onChange={e => setDirection(e.target.value)}
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                                <option value="outbound">Outbound (we contacted them)</option>
                                <option value="inbound">Inbound (they reached out)</option>
                            </select>
                        </div>
                    </div>
                    {type === 'email' && (
                        <div className="space-y-1">
                            <Label className="text-xs">Subject</Label>
                            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line" className="text-sm h-9" />
                        </div>
                    )}
                    <div className="space-y-1">
                        <Label className="text-xs">Notes / Body <span className="text-red-500">*</span></Label>
                        <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="What happened? Key takeaways, what was said…"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Outcome</Label>
                            <select value={outcome} onChange={e => setOutcome(e.target.value)}
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                                <option value="">— Select outcome</option>
                                <option value="positive">✅ Positive</option>
                                <option value="neutral">➖ Neutral</option>
                                <option value="negative">❌ Negative</option>
                                <option value="no_response">📭 No Response</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Next Follow-Up Date</Label>
                            <input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Move to Stage (optional)</Label>
                        <select value={statusUpd} onChange={e => setStatusUpd(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                            <option value="">— Keep current stage</option>
                            {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_MAP[s]?.label || s}</option>)}
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="gap-2">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            {saving ? 'Saving…' : 'Save Log'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Send Demo Email Modal ────────────────────────────────────────────────────
function SendDemoEmailModal({ lead, onClose, onSent }) {
    const [subject, setSubject] = useState(`Let's show you how EchoHire can transform hiring at ${lead.name}`);
    const [slots, setSlots]     = useState('');
    const [demoDate, setDemoDate] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!subject.trim()) { toast.error('Subject is required.'); return; }
        setSending(true);
        try {
            const proposedSlots = slots.split('\n').map(s => s.trim()).filter(Boolean);
            await apiClient.sendProspectDemoEmail(lead._id, {
                subject: subject.trim(),
                proposedSlots,
                // datetime-local has no timezone and the server runs UTC, so
                // send an absolute instant rather than the raw wall-clock text.
                demoDate: demoDate ? new Date(demoDate).toISOString() : null,
            });
            toast.success('Demo email sent!');
            onSent?.();
            onClose();
        } catch (e) { toast.error(e.message || 'Failed to send.'); }
        finally { setSending(false); }
    };

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> Send Demo Email — {lead.name}</DialogTitle>
                    <DialogDescription>To: <strong>{lead.email}</strong></DialogDescription>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                    <div className="space-y-1">
                        <Label className="text-xs">Subject</Label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-sm h-9" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Proposed Time Slots <span className="text-muted-foreground">(one per line)</span></Label>
                        <textarea value={slots} onChange={e => setSlots(e.target.value)} rows={3}
                            placeholder={`Mon 16 June, 10:00 AM IST\nTue 17 June, 3:00 PM IST\nWed 18 June, 11:00 AM IST`}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
                        <p className="text-[10px] text-muted-foreground">Leave blank to let them suggest a time.</p>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Confirmed Demo Date (if already agreed)</Label>
                        <input type="datetime-local" value={demoDate} onChange={e => setDemoDate(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                        <p className="text-[10px] text-muted-foreground">If set, lead status moves to "Demo Scheduled" automatically.</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                        <Button onClick={handleSend} disabled={sending} className="gap-2">
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
                            {sending ? 'Sending…' : 'Send Demo Email'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Outreach Email Modal ─────────────────────────────────────────────────────
function OutreachModal({ lead, onClose, onSent }) {
    const [subject, setSubject] = useState(`Streamline hiring at ${lead.name} with EchoHire AI`);
    const greeting = lead.contactPerson && lead.contactPerson.trim().length > 1
        ? `Hi ${lead.contactPerson.trim()},`
        : `Hi ${lead.name} team,`;
    const [body, setBody] = useState(
`${greeting}

I came across ${lead.name} (${lead.domain}) and was impressed by what you're building in the ${lead.industry || 'industry'} space.

At EchoHire, we help fast-growing companies like yours run AI-powered video interviews at scale — cutting time-to-hire by 60% and letting your team focus only on the best candidates.

Would you be open to a quick 15-min demo this week?
👉 Book a time directly: https://echohire.in/book-demo

Or just reply to this email and we'll sort something out.

Best,
EchoHire Team
https://echohire.in`);
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required.'); return; }
        setSending(true);
        try {
            await apiClient.sendProspectOutreach(lead._id, { subject, body });
            toast.success('Outreach sent and logged!');
            onSent?.();
            onClose();
        } catch (e) { toast.error(e.message || 'Failed.'); }
        finally { setSending(false); }
    };

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Send Outreach — {lead.name}</DialogTitle>
                    <DialogDescription>To: <strong>{lead.email}</strong></DialogDescription>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                    <div className="space-y-1">
                        <Label className="text-xs">Subject</Label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-sm h-9" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Body</Label>
                        <textarea value={body} onChange={e => setBody(e.target.value)} rows={11}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                        <Button onClick={handleSend} disabled={sending} className="gap-2">
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            {sending ? 'Sending…' : 'Send Email'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Smart Pagination ─────────────────────────────────────────────────────────
function ProspectPagination({ page, totalPages, total, onPageChange }) {
    const [jumpVal, setJumpVal] = useState('');

    if (totalPages <= 1) return null;

    // Build the page-number window: always show 1, always show totalPages,
    // show current ±2, fill gaps with ellipsis
    function buildPages(cur, total) {
        const set  = new Set([1, total, cur - 2, cur - 1, cur, cur + 1, cur + 2]
            .filter(n => n >= 1 && n <= total));
        const sorted = [...set].sort((a, b) => a - b);
        const out = [];
        let prev = null;
        for (const n of sorted) {
            if (prev !== null && n - prev > 1) out.push('…');
            out.push(n);
            prev = n;
        }
        return out;
    }

    const pages = buildPages(page, totalPages);
    const start = (page - 1) * 25 + 1;
    const end   = Math.min(page * 25, total);

    const go = (p) => { if (p >= 1 && p <= totalPages && p !== page) onPageChange(p); };

    const handleJump = (e) => {
        if (e.key === 'Enter') {
            const n = parseInt(jumpVal, 10);
            if (!isNaN(n)) go(n);
            setJumpVal('');
        }
    };

    return (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-2.5">
            {/* Left: record range */}
            <p className="text-xs text-muted-foreground shrink-0">
                Showing <span className="font-semibold text-foreground">{start}–{end}</span> of{' '}
                <span className="font-semibold text-foreground">{total}</span> prospects
            </p>

            {/* Centre: page buttons */}
            <div className="flex items-center gap-0.5">
                {/* First + Prev */}
                <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-[11px]"
                    disabled={page <= 1} onClick={() => go(1)} title="First page">
                    <ChevronLeft className="h-3 w-3 -mr-1" /><ChevronLeft className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0 mr-1"
                    disabled={page <= 1} onClick={() => go(page - 1)} title="Previous page">
                    <ChevronLeft className="h-3.5 w-3.5" />
                </Button>

                {/* Numbered pages */}
                {pages.map((p, i) =>
                    p === '…'
                        ? <span key={`e${i}`} className="px-1 text-xs text-muted-foreground select-none">…</span>
                        : <button key={p} type="button"
                            onClick={() => go(p)}
                            className={cn(
                                'h-7 min-w-[28px] rounded-md border px-1.5 text-xs font-semibold transition-colors',
                                p === page
                                    ? 'border-primary bg-primary text-primary-foreground shadow-sm cursor-default'
                                    : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                          >{p}</button>
                )}

                {/* Next + Last */}
                <Button variant="outline" size="sm" className="h-7 w-7 p-0 ml-1"
                    disabled={page >= totalPages} onClick={() => go(page + 1)} title="Next page">
                    <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-[11px]"
                    disabled={page >= totalPages} onClick={() => go(totalPages)} title="Last page">
                    <ChevronRight className="h-3 w-3 -mr-1" /><ChevronRight className="h-3 w-3" />
                </Button>
            </div>

            {/* Right: jump-to input */}
            <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-muted-foreground">Go to</span>
                <input
                    type="number" min={1} max={totalPages}
                    value={jumpVal}
                    onChange={e => setJumpVal(e.target.value)}
                    onKeyDown={handleJump}
                    placeholder={String(page)}
                    className="h-7 w-14 rounded-md border border-input bg-background px-2 text-xs text-center font-semibold focus:outline-none focus:ring-2 focus:ring-ring [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
            </div>
        </div>
    );
}

// ─── Bulk Insert Modal ────────────────────────────────────────────────────────
// ── BulkInsertModal — module-level constants (no re-creation per render) ───────
const BULK_SAMPLE = [{
    name: 'Docsumo', domain: 'docsumo.com', industry: 'SaaS',
    size: '51-100', tier: 1, funding_stage: 'Seed', open_roles_count: 8,
    city: 'Mumbai', country: 'India', website: 'https://docsumo.com',
    linkedin_url: 'https://linkedin.com/company/docsumoai',
    careers_email: 'careers@docsumo.com',
    email_format: '{first}.{last}@docsumo.com',
    source: 'apollo', why: 'Series A, actively hiring',
    contacts: [
        { first_name: 'Shruti', last_name: 'Ghandikota', title: 'Talent Acquisition',
          persona: 'recruiter', email: 'shruti@docsumo.com', is_primary: true },
        { first_name: 'Akash', title: 'Chief of Staff',
          email: 'akash@docsumo.com', is_decision_maker: true },
    ],
}];
const BULK_SAMPLE_JSON = JSON.stringify(BULK_SAMPLE, null, 2);

const CSV_COLS = [
    'name', 'domain', 'website', 'city', 'state', 'country',
    'industry', 'size', 'funding_stage', 'open_roles_count',
    'tier', 'source', 'email_format', 'careers_email', 'linkedin_url',
    'notes', 'why', 'deal_value',
    'contact_first_name', 'contact_last_name', 'contact_title',
    'contact_email', 'contact_mobile', 'contact_persona',
];
const CSV_SAMPLE_ROW = {
    name: 'Docsumo', domain: 'docsumo.com', website: 'https://docsumo.com',
    city: 'Mumbai', state: 'Maharashtra', country: 'India',
    industry: 'SaaS', size: '51-100', funding_stage: 'Seed',
    open_roles_count: '8', tier: '1', source: 'apollo',
    email_format: '{first}.{last}@docsumo.com',
    careers_email: 'careers@docsumo.com',
    linkedin_url: 'https://linkedin.com/company/docsumoai',
    notes: '', why: 'Series A actively hiring', deal_value: '',
    contact_first_name: 'Shruti', contact_last_name: 'Ghandikota',
    contact_title: 'Talent Acquisition', contact_email: 'shruti@docsumo.com',
    contact_mobile: '', contact_persona: 'recruiter',
};

function csvEsc(v) {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
}
function buildSampleCsv() {
    return [CSV_COLS.join(','), CSV_COLS.map(c => csvEsc(CSV_SAMPLE_ROW[c] ?? '')).join(',')].join('\n');
}
function parseCsvText(text) {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) throw new Error('Need a header row and at least one data row.');
    function parseLine(line) {
        const vals = []; let cur = '', inQ = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else { inQ = !inQ; } }
            else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
            else { cur += ch; }
        }
        vals.push(cur.trim()); return vals;
    }
    const headers = parseLine(lines[0]).map(h => h.replace(/^"|"$/g, '').toLowerCase().trim());
    return lines.slice(1).map(line => {
        const row = {}; parseLine(line).forEach((v, i) => { row[headers[i]] = v || ''; });
        const obj = {};
        ['name','domain','website','city','state','country','industry','size',
         'funding_stage','open_roles_count','tier','source','email_format',
         'careers_email','linkedin_url','notes','why','deal_value',
        ].forEach(k => { if (row[k]) obj[k] = row[k]; });
        if (row.contact_email || row.contact_first_name) {
            obj.contacts = [{ first_name: row.contact_first_name || '', last_name: row.contact_last_name || '',
                title: row.contact_title || '', email: row.contact_email || '',
                mobile: row.contact_mobile || '', persona: row.contact_persona || '',
                is_primary: true, is_decision_maker: false }];
        }
        return obj;
    }).filter(o => o.name && o.domain);
}

function BulkInsertModal({ onClose, onInserted }) {
    const [mode, setMode]             = useState('json');  // 'json' | 'csv'
    const [raw, setRaw]               = useState('');
    const [showSample, setShowSample] = useState(true);
    const [csvFile, setCsvFile]       = useState(null);
    const [inserting, setInserting]   = useState(false);
    const [result, setResult]         = useState(null);
    const fileRef                     = useRef(null);

    const handleCopyJson = () => {
        navigator.clipboard.writeText(BULK_SAMPLE_JSON)
            .then(() => toast.success('Sample JSON copied!'))
            .catch(() => toast.error('Copy failed — try manually selecting the text.'));
    };

    const handleDownloadCsv = () => {
        const blob = new Blob([buildSampleCsv()], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = 'prospects_sample.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const handleInsert = async () => {
        let parsed;
        if (mode === 'json') {
            if (!raw.trim()) { toast.error('Paste some JSON first.'); return; }
            try { parsed = JSON.parse(raw.trim()); }
            catch { toast.error('Invalid JSON — check syntax and try again.'); return; }
        } else {
            if (!csvFile) { toast.error('Select a CSV file first.'); return; }
            try {
                const text = await csvFile.text();
                parsed = parseCsvText(text);
                if (!parsed.length) { toast.error('No valid rows found — ensure name & domain columns are present.'); return; }
            } catch (e) { toast.error('CSV parse error: ' + e.message); return; }
        }
        if (!Array.isArray(parsed)) parsed = [parsed];
        setInserting(true);
        try {
            const r = await apiClient.upsertProspectLeads(parsed);
            setResult(r.data);
            toast.success(`Done: ${r.data.inserted} new, ${r.data.updated} updated.`);
            onInserted?.();
        } catch (e) { toast.error(e.message || 'Import failed.'); }
        finally { setInserting(false); }
    };

    const canImport = mode === 'json' ? !!raw.trim() : !!csvFile;

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Plus className="h-4 w-4 text-primary" /> Bulk Insert / Upsert Prospects
                    </DialogTitle>
                    <DialogDescription>
                        Import via JSON paste or CSV upload. Existing records matching the same domain are updated; new ones are created.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 pt-2">

                    {/* ── Mode switcher ── */}
                    <div className="flex rounded-lg border border-border bg-muted/30 p-1 gap-1 w-fit">
                        {[
                            { k: 'json', Icon: FileJson,        label: 'JSON Paste' },
                            { k: 'csv',  Icon: FileSpreadsheet, label: 'CSV Upload' },
                        ].map(({ k, Icon, label }) => (
                            <button key={k} type="button"
                                onClick={() => { setMode(k); setResult(null); setShowSample(true); }}
                                className={cn(
                                    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
                                    mode === k
                                        ? 'bg-background text-foreground shadow-sm border border-border'
                                        : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                <Icon className="h-3.5 w-3.5" />{label}
                            </button>
                        ))}
                    </div>

                    {/* ── JSON mode ── */}
                    {mode === 'json' && <>
                        {/* Collapsible sample panel */}
                        <div className="rounded-lg border border-border overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b border-border">
                                <button type="button"
                                    onClick={() => setShowSample(p => !p)}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-foreground hover:text-primary transition-colors"
                                >
                                    <FileJson className="h-3.5 w-3.5 text-primary" />
                                    Sample JSON
                                    <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform duration-200', !showSample && '-rotate-90')} />
                                </button>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] gap-1"
                                        onClick={handleCopyJson}>
                                        <Copy className="h-3 w-3" /> Copy
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] gap-1"
                                        onClick={() => setRaw(BULK_SAMPLE_JSON)}>
                                        <ArrowRight className="h-3 w-3" /> Use as template
                                    </Button>
                                </div>
                            </div>
                            {showSample && (
                                <pre className="p-3 text-[11px] font-mono text-muted-foreground leading-relaxed overflow-x-auto bg-muted/20 max-h-56 whitespace-pre">
{BULK_SAMPLE_JSON}
                                </pre>
                            )}
                        </div>

                        {/* Paste area */}
                        <div className="space-y-1.5">
                            <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                Paste JSON
                            </Label>
                            <Textarea
                                value={raw}
                                onChange={e => setRaw(e.target.value)}
                                rows={10}
                                placeholder={'Paste a JSON object {...} or array [{...}, {...}] here…'}
                                className="font-mono text-sm resize-y"
                            />
                        </div>
                    </>}

                    {/* ── CSV mode ── */}
                    {mode === 'csv' && <>
                        {/* Columns reference + download */}
                        <div className="rounded-lg border border-border overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b border-border">
                                <button type="button"
                                    onClick={() => setShowSample(p => !p)}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-foreground hover:text-primary transition-colors"
                                >
                                    <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-500" />
                                    CSV Columns
                                    <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform duration-200', !showSample && '-rotate-90')} />
                                </button>
                                <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1"
                                    onClick={handleDownloadCsv}>
                                    <Download className="h-3 w-3" /> Download Sample CSV
                                </Button>
                            </div>
                            {showSample && (
                                <div className="p-3 bg-muted/20 space-y-2">
                                    <div className="flex flex-wrap gap-1">
                                        {CSV_COLS.map(col => (
                                            <span key={col} className={cn(
                                                'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold border',
                                                col === 'name' || col === 'domain'
                                                    ? 'bg-primary/10 text-primary border-primary/20'
                                                    : col.startsWith('contact_')
                                                        ? 'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20'
                                                        : 'bg-muted text-muted-foreground border-border'
                                            )}>
                                                {col}
                                                {(col === 'name' || col === 'domain') && <span className="ml-0.5 text-red-500">*</span>}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                                        <span className="font-mono font-semibold text-primary">name</span> and{' '}
                                        <span className="font-mono font-semibold text-primary">domain</span> are required.{' '}
                                        <span className="font-mono font-semibold text-violet-600">contact_*</span> columns
                                        populate the primary contact. One row = one company. Download the sample to see exact formatting.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* File drop zone */}
                        <div
                            onClick={() => fileRef.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                                e.preventDefault();
                                const f = e.dataTransfer.files[0];
                                if (f?.name.toLowerCase().endsWith('.csv')) setCsvFile(f);
                                else toast.error('Only .csv files accepted.');
                            }}
                            className={cn(
                                'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed',
                                'px-6 py-10 cursor-pointer transition-colors select-none',
                                csvFile
                                    ? 'border-emerald-500/50 bg-emerald-500/5'
                                    : 'border-border hover:border-primary/50 hover:bg-muted/30',
                            )}
                        >
                            <input ref={fileRef} type="file" accept=".csv" className="hidden"
                                onChange={e => setCsvFile(e.target.files?.[0] || null)} />
                            {csvFile ? (
                                <>
                                    <FileSpreadsheet className="h-9 w-9 text-emerald-500" />
                                    <div className="text-center">
                                        <p className="text-sm font-semibold text-foreground">{csvFile.name}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {(csvFile.size / 1024).toFixed(1)} KB · Click to change file
                                        </p>
                                    </div>
                                    <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground gap-1"
                                        onClick={e => { e.stopPropagation(); setCsvFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                                        <X className="h-3 w-3" /> Remove
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Upload className="h-9 w-9 text-muted-foreground/40" />
                                    <div className="text-center">
                                        <p className="text-sm font-semibold text-foreground">Drop CSV here or click to browse</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Must match the column format above.{' '}
                                            <button type="button" onClick={e => { e.stopPropagation(); handleDownloadCsv(); }}
                                                className="text-primary underline underline-offset-2 hover:no-underline">
                                                Download sample
                                            </button>{' '}to get started.
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </>}

                    {/* Result banner */}
                    {result && (
                        <div className="flex flex-wrap gap-4 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3 text-sm">
                            <span className="text-emerald-700 dark:text-emerald-400 font-medium">✓ Inserted: <strong>{result.inserted}</strong></span>
                            <span className="text-blue-700 dark:text-blue-400 font-medium">↻ Updated: <strong>{result.updated}</strong></span>
                            {result.errors > 0 && <span className="text-red-600 font-medium">✗ Errors: <strong>{result.errors}</strong></span>}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between gap-3 pt-1 border-t border-border">
                        <p className="text-[11px] text-muted-foreground">
                            {mode === 'json'
                                ? 'Accepts {} or [{}]. Domain is the unique key — matching records are updated, new ones created.'
                                : 'One row per company. name + domain required. Use JSON for multi-contact imports.'}
                        </p>
                        <div className="flex gap-2 shrink-0">
                            <Button variant="outline" onClick={onClose} disabled={inserting}>Cancel</Button>
                            <Button onClick={handleInsert} disabled={inserting || !canImport} className="gap-2">
                                {inserting
                                    ? <><Loader2 className="h-4 w-4 animate-spin" />Importing…</>
                                    : <><Upload className="h-4 w-4" />Import {mode === 'json' ? 'JSON' : 'CSV'}</>}
                            </Button>
                        </div>
                    </div>

                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Bulk Email Modal ─────────────────────────────────────────────────────────
const DRIP_TEMPLATE_TYPES = [
    { value: 'cold_outreach',     label: '📧 Cold Outreach' },
    { value: 'followup_1',        label: '🔁 Follow-Up #1' },
    { value: 'followup_2',        label: '📈 Follow-Up #2' },
    { value: 'followup_3',        label: '🏁 Follow-Up #3 (Final)' },
    { value: 'demo_request',      label: '📅 Demo Request' },
    { value: 'post_demo',         label: '✅ Post-Demo' },
    { value: 'proposal_followup', label: '📑 Proposal Follow-Up' },
];

function BulkEmailModal({ leadIds, onClose, onDone }) {
    const [templateType, setTemplateType] = useState('cold_outreach');
    const [sending, setSending] = useState(false);
    const [result, setResult]   = useState(null);

    const handleSend = async () => {
        setSending(true);
        try {
            const r = await apiClient.bulkEmailProspects({ leadIds, templateType });
            setResult(r.data);
            toast.success(`Sent to ${r.data.sent} lead(s). Failed: ${r.data.failed}`);
            onDone?.();
        } catch (e) { toast.error(e.message || 'Bulk email failed.'); }
        finally { setSending(false); }
    };

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-primary" /> Bulk Email — {leadIds.length} lead(s)
                    </DialogTitle>
                    <DialogDescription>Select a template. The system will replace variables with each lead's data automatically.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                    <div className="space-y-1">
                        <Label className="text-xs">Email Template</Label>
                        <select value={templateType} onChange={e => setTemplateType(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                            {DRIP_TEMPLATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
                        ⚠️ This will send an email to each selected lead that has a valid email address and has not unsubscribed.
                    </div>
                    {result && (
                        <div className="flex gap-4 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3 text-sm">
                            <span className="text-emerald-700 dark:text-emerald-400">✓ Sent: <strong>{result.sent}</strong></span>
                            {result.failed > 0 && <span className="text-red-600">✗ Failed: <strong>{result.failed}</strong></span>}
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                        <Button onClick={handleSend} disabled={sending} className="gap-2">
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            {sending ? 'Sending…' : `Send to ${leadIds.length} Lead(s)`}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Lead Detail Drawer ───────────────────────────────────────────────────────
const LOG_OUTCOME_COLOR = { positive: 'text-emerald-600', neutral: 'text-muted-foreground', negative: 'text-red-500', no_response: 'text-amber-500', '': '' };

function LeadDetail({ lead: initLead, onClose, onRefresh }) {
    const [lead, setLead]               = useState(initLead);
    const [activeTab, setActiveTab]     = useState('overview');
    const [updatingStatus, setUpdStatus] = useState(false);
    const [showEdit, setShowEdit]       = useState(false);
    const [showOutreach, setShowOutreach] = useState(false);
    const [showDemo, setShowDemo]       = useState(false);
    const [showLog, setShowLog]         = useState(false);
    const [logPrefill, setLogPrefill]   = useState(null);
    const [showNextEdit, setShowNextEdit] = useState(false);
    const [naForm, setNaForm]           = useState({ type: lead.nextAction?.type || 'email', date: lead.nextAction?.date ? new Date(lead.nextAction.date).toISOString().split('T')[0] : '', notes: lead.nextAction?.notes || '' });
    const [savingNA, setSavingNA]       = useState(false);
    const [togglingSeq, setTogglingSeq]   = useState(false);
    const [showDripPicker, setShowDripPicker] = useState(false);

    const reload = async () => {
        try {
            const r = await apiClient.getProspectLead(lead._id);
            setLead(r.data);
            onRefresh?.();
        } catch { /* silent */ }
    };

    const handleStatusChange = async (newStatus) => {
        setUpdStatus(true);
        try {
            const r = await apiClient.updateProspectLead(lead._id, { status: newStatus });
            setLead(r.data);
            onRefresh?.();
            toast.success('Stage updated.');
        } catch (e) { toast.error(e.message || 'Failed.'); }
        finally { setUpdStatus(false); }
    };

    const handleSaveNA = async () => {
        setSavingNA(true);
        try {
            const r = await apiClient.updateProspectNextAction(lead._id, naForm);
            setLead(r.data);
            setShowNextEdit(false);
            toast.success('Next action saved.');
        } catch (e) { toast.error(e.message || 'Failed.'); }
        finally { setSavingNA(false); }
    };

    const handleToggleSequence = async () => {
        if (!lead.sequence?.active) {
            // Show sequence picker modal instead of directly starting
            setShowDripPicker(true);
            return;
        }
        setTogglingSeq(true);
        try {
            if (lead.sequence?.active) {
                const r = await apiClient.stopProspectSequence(lead._id, 'manual');
                setLead(r.data);
                toast.success('Drip sequence stopped.');
            }
            onRefresh?.();
        } catch (e) { toast.error(e.message || 'Failed.'); }
        finally { setTogglingSeq(false); }
    };

    const handleCtaAction = (action) => {
        if (action === 'outreach') setShowOutreach(true);
        else if (action === 'demo') setShowDemo(true);
        else if (action === 'log') { setLogPrefill(null); setShowLog(true); }
    };

    // ── Fix-email inline form ──────────────────────────────────────────────────
    const [fixEmailLogId, setFixEmailLogId] = useState(null); // which log entry triggered it
    const [fixEmailVal, setFixEmailVal]     = useState('');
    const [fixEmailSaving, setFixEmailSaving] = useState(false);

    const handleFixEmail = async () => {
        if (!fixEmailVal.trim()) return;
        setFixEmailSaving(true);
        try {
            const r = await apiClient.updateProspectLead(lead._id, { email: fixEmailVal.trim() });
            setLead(r.data);
            setFixEmailLogId(null);
            setFixEmailVal('');
            toast.success('Email updated.');
            onRefresh?.();
        } catch (e) { toast.error(e.message || 'Failed.'); }
        finally { setFixEmailSaving(false); }
    };

    // ── Expanded body per log entry ────────────────────────────────────────────
    const [expandedLogs, setExpandedLogs] = useState(new Set());
    const toggleExpand = (id) => setExpandedLogs(prev => {
        const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });

    // Merge legacy outreachHistory + followUps + new contactLogs into one timeline
    const allLogs = [
        ...(lead.outreachHistory || []).filter(o => !(lead.contactLogs || []).some(c => c.subject === o.subject && c.body === o.body))
            .map(o => ({ _id: o._id, loggedAt: o.sentAt, type: 'email', direction: 'outbound', subject: o.subject, body: o.body, loggedBy: o.sentBy })),
        ...(lead.followUps || []).filter(f => !(lead.contactLogs || []).some(c => c.body === f.notes))
            .map(f => ({ _id: f._id, loggedAt: f.loggedAt, type: f.type, direction: 'outbound', body: f.notes, outcome: f.outcome, nextFollowUpDate: f.nextFollowUpDate, loggedBy: f.loggedBy })),
        ...(lead.contactLogs || []),
    ].sort((a, b) => new Date(b.loggedAt) - new Date(a.loggedAt));

    const suggestion = lead.suggestion;
    const isOverdue  = lead.nextAction?.date && new Date(lead.nextAction.date) < new Date();

    const TABS = [
        { key: 'overview', label: 'Overview' },
        { key: 'activity', label: `Activity (${allLogs.length})` },
        { key: 'pipeline', label: 'Pipeline' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
            <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative z-10 h-full w-full max-w-xl overflow-y-auto bg-background shadow-2xl border-l border-border flex flex-col">
                {/* Header */}
                <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-5 py-3">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <h2 className="font-bold text-foreground text-base">{lead.name}</h2>
                            <div className="flex items-center gap-2 mt-0.5">
                                <a href={`https://${lead.domain}`} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-primary">
                                    {lead.domain} <ExternalLink className="h-3 w-3" />
                                </a>
                                <StatusBadge status={lead.status} />
                                <TierStars tier={lead.tier} />
                            </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => setShowEdit(true)}>
                                <Pencil className="h-3 w-3" /> Edit
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><X className="h-4 w-4" /></Button>
                        </div>
                    </div>
                    {/* Tabs */}
                    <div className="flex gap-0 mt-3 -mb-px">
                        {TABS.map(t => (
                            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                                className={cn('px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors',
                                    activeTab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 p-5 space-y-4">
                    {/* ── OVERVIEW TAB ─── */}
                    {activeTab === 'overview' && <>
                        {/* Suggestion */}
                        {suggestion && <SuggestionCard suggestion={suggestion} onCtaClick={handleCtaAction} />}

                        {/* Next Action */}
                        <div className="rounded-lg border border-border bg-muted/20 p-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Next Action</span>
                                <button type="button" onClick={() => setShowNextEdit(v => !v)}
                                    className="text-[10px] text-primary font-semibold hover:underline">{showNextEdit ? 'Cancel' : 'Edit'}</button>
                            </div>
                            {!showNextEdit ? (
                                <div className="flex items-center gap-2">
                                    {lead.nextAction?.type && lead.nextAction.type !== 'none'
                                        ? <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize',
                                            isOverdue ? 'border-red-400/40 bg-red-500/10 text-red-600' : 'border-border bg-muted text-muted-foreground')}>
                                            {isOverdue && <AlertTriangle className="h-2.5 w-2.5" />}
                                            {lead.nextAction.type}
                                            {lead.nextAction.date && ` — ${new Date(lead.nextAction.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                                          </span>
                                        : <span className="text-xs text-muted-foreground">Not set — use suggestion above.</span>}
                                    {lead.nextAction?.notes && <span className="text-xs text-muted-foreground">· {lead.nextAction.notes}</span>}
                                </div>
                            ) : (
                                <div className="space-y-2 mt-1">
                                    <div className="grid grid-cols-2 gap-2">
                                        <select value={naForm.type} onChange={e => setNaForm(f => ({ ...f, type: e.target.value }))}
                                            className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring">
                                            {['email','call','linkedin','demo','proposal','meeting','none'].map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                                        </select>
                                        <input type="date" value={naForm.date} onChange={e => setNaForm(f => ({ ...f, date: e.target.value }))}
                                            className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring" />
                                    </div>
                                    <Input value={naForm.notes} onChange={e => setNaForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes about this action…" className="h-8 text-xs" />
                                    <Button size="sm" onClick={handleSaveNA} disabled={savingNA} className="h-7 text-xs gap-1">
                                        {savingNA ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Quick info grid */}
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { icon: Globe,     label: 'Industry',     value: lead.industry || '—' },
                                { icon: Users,     label: 'Size',         value: lead.size || '—' },
                                { icon: MapPin,    label: 'Location',     value: [lead.city, lead.country].filter(Boolean).join(', ') || '—' },
                                { icon: TrendingUp,label: 'Funding',      value: lead.fundingStage || '—' },
                                { icon: Briefcase, label: 'Open Roles',   value: lead.openRolesCount > 0 ? `${lead.openRolesCount} open roles` : '—' },
                                { icon: Mail,      label: 'Careers Email',value: lead.careersEmail || lead.email || '—' },
                            ].map(({ icon: Icon, label, value }) => (
                                <div key={label} className="rounded-lg border border-border bg-muted/20 p-2.5">
                                    <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">
                                        <Icon className="h-2.5 w-2.5" />{label}
                                    </div>
                                    <div className="text-xs font-medium text-foreground truncate" title={value}>{value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Contacts */}
                        {(lead.contacts?.length > 0) && (
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Contacts ({lead.contacts.length})</p>
                                {lead.contacts.map((c, i) => (
                                    <div key={c._id || i} className={cn('rounded-lg border p-2.5 flex items-start gap-2.5', c.isPrimary ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/20')}>
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-foreground">
                                            {(c.firstName?.[0] || c.fullName?.[0] || '?').toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-xs font-semibold text-foreground">
                                                    {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.fullName || '—'}
                                                </span>
                                                {c.isPrimary && <span className="text-[9px] font-bold bg-primary/15 text-primary rounded px-1.5 py-0.5">PRIMARY</span>}
                                                {c.isDecisionMaker && <span className="text-[9px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 rounded px-1.5 py-0.5">DM</span>}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground">{c.title || '—'}</div>
                                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                                                {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-0.5 text-[11px] text-primary hover:underline"><Mail className="h-2.5 w-2.5" />{c.email}</a>}
                                                {c.mobile && <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground"><Phone className="h-2.5 w-2.5" />{c.mobile}</span>}
                                                {c.linkedinUrl && <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:underline">LinkedIn</a>}
                                            </div>
                                            {(c.persona || c.seniority) && (
                                                <div className="flex gap-1 mt-1">
                                                    {c.persona   && <span className="text-[9px] bg-muted rounded px-1.5 py-0.5 text-muted-foreground capitalize">{c.persona.replace(/_/g,' ')}</span>}
                                                    {c.seniority && <span className="text-[9px] bg-muted rounded px-1.5 py-0.5 text-muted-foreground capitalize">{c.seniority.replace(/_/g,' ')}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Deal info */}
                        {(lead.dealValue > 0 || lead.expectedCloseDate || lead.demoScheduledAt) && (
                            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-1.5">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Deal</p>
                                {lead.dealValue > 0 && <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">₹{lead.dealValue.toLocaleString('en-IN')}/mo</p>}
                                {lead.expectedCloseDate && <p className="text-xs text-muted-foreground">Expected close: {new Date(lead.expectedCloseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                                {lead.demoScheduledAt && <p className="text-xs text-cyan-600 dark:text-cyan-400">Demo: {new Date(lead.demoScheduledAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
                            </div>
                        )}

                        {/* Why / qualifier */}
                        {lead.why && (
                            <div className="rounded-lg border border-border bg-muted/20 p-3">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Why This Lead</p>
                                <p className="text-xs text-foreground leading-relaxed">{lead.why}</p>
                            </div>
                        )}

                        {/* ── Automated Drip Sequence ── */}
                        {(() => {
                            const seq       = lead.sequence || {};
                            const step      = seq.step || 0;
                            const completed = seq.stoppedReason === 'completed';
                            const canStart  = ['new','researching','contacted'].includes(lead.status)
                                              && !lead.unsubscribed && !!lead.email;

                            const STEPS = [
                                { label: 'Cold Outreach', short: 'Cold' },
                                { label: 'Follow-Up #1',  short: 'FU 1' },
                                { label: 'Follow-Up #2',  short: 'FU 2' },
                                { label: 'Follow-Up #3',  short: 'FU 3' },
                            ];

                            let statusLabel, statusCls, cardCls;
                            if (lead.unsubscribed) {
                                statusLabel = 'Unsubscribed'; statusCls = 'bg-red-500/10 text-red-600 border-red-500/20';    cardCls = 'border-red-500/20 bg-red-500/5';
                            } else if (seq.active) {
                                statusLabel = 'Active';       statusCls = 'bg-violet-500/10 text-violet-600 border-violet-500/20'; cardCls = 'border-violet-500/30 bg-violet-500/5';
                            } else if (completed) {
                                statusLabel = 'Completed';    statusCls = 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'; cardCls = 'border-emerald-500/20 bg-emerald-500/5';
                            } else if (seq.stoppedAt) {
                                statusLabel = 'Stopped';      statusCls = 'bg-amber-500/10 text-amber-600 border-amber-500/20';  cardCls = 'border-amber-500/20 bg-amber-500/5';
                            } else {
                                statusLabel = 'Not Started';  statusCls = 'bg-muted text-muted-foreground border-border';         cardCls = 'border-border bg-muted/20';
                            }

                            return (
                                <div className={cn('rounded-lg border p-3 space-y-3', cardCls)}>
                                    {/* Header row */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                                Drip Sequence
                                            </span>
                                            <span className={cn('inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold leading-none', statusCls)}>
                                                {statusLabel}
                                            </span>
                                        </div>
                                        {/* Start / Stop button */}
                                        {lead.unsubscribed ? (
                                            <span className="text-[11px] font-semibold text-red-500 shrink-0">Opted out</span>
                                        ) : !lead.email ? (
                                            <span className="text-[11px] text-muted-foreground shrink-0">No email</span>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant={seq.active ? 'destructive' : 'default'}
                                                className="h-7 text-xs gap-1 shrink-0"
                                                onClick={handleToggleSequence}
                                                disabled={togglingSeq || (!canStart && !seq.active)}
                                                title={!canStart && !seq.active ? 'Only available for New / Researching / Contacted leads' : undefined}
                                            >
                                                {togglingSeq
                                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                                    : seq.active
                                                        ? <><StopCircle className="h-3 w-3" />Stop</>
                                                        : <><Play className="h-3 w-3" />Start</>}
                                            </Button>
                                        )}
                                    </div>

                                    {/* Status detail */}
                                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                                        {seq.active && (
                                            <>
                                                Step {step + 1} of 4 sending
                                                {seq.nextSendAt && <> · Next: <span className="font-semibold text-foreground">
                                                    {new Date(seq.nextSendAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </span></>}
                                                {seq.startedAt && <> · Started {new Date(seq.startedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</>}
                                            </>
                                        )}
                                        {!seq.active && completed && 'All 4 emails sent successfully.'}
                                        {!seq.active && seq.stoppedAt && !completed && (
                                            <>
                                                Stopped {new Date(seq.stoppedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                {seq.stoppedReason && seq.stoppedReason !== 'manual' && <> — {seq.stoppedReason}</>}
                                                {seq.step > 0 && <> ({seq.step} of 4 sent)</>}
                                            </>
                                        )}
                                        {!seq.active && !seq.stoppedAt && !lead.unsubscribed && lead.email &&
                                            'Not started — sends 4 automated outreach emails over ~12 days.'}
                                        {lead.unsubscribed && lead.unsubscribedAt && (
                                            <>Opted out on {new Date(lead.unsubscribedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                                        )}
                                    </p>

                                    {/* Step progress pills */}
                                    <div className="grid grid-cols-4 gap-1">
                                        {STEPS.map(({ short }, i) => {
                                            const isDone    = completed || (i < step);
                                            const isCurrent = seq.active && i === step;
                                            return (
                                                <div key={i} className={cn(
                                                    'rounded-md text-center py-2 px-1 transition-all',
                                                    isCurrent ? 'bg-violet-500/20 ring-1 ring-violet-500/50 text-violet-700 dark:text-violet-300'
                                                    : isDone   ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                                    :            'bg-muted/60 text-muted-foreground/50'
                                                )}>
                                                    <div className="text-[8px] font-medium opacity-60 mb-0.5">Step {i + 1}</div>
                                                    <div className="text-[9px] font-bold uppercase tracking-wide">
                                                        {isDone && !isCurrent ? '✓ ' : isCurrent ? '▶ ' : ''}{short}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Eligibility warning */}
                                    {!canStart && !seq.active && !lead.unsubscribed && lead.email && (
                                        <p className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                                            <AlertTriangle className="h-3 w-3 shrink-0" />
                                            Only available for New, Researching, or Contacted leads.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2">
                            {lead.email && <>
                                <Button size="sm" variant="default" className="gap-1.5 h-8 text-xs" onClick={() => setShowOutreach(true)}>
                                    <Send className="h-3 w-3" /> Outreach Email
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={() => setShowDemo(true)}>
                                    <Calendar className="h-3 w-3" /> Demo Email
                                </Button>
                            </>}
                            <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={() => setShowLog(true)}>
                                <MessageSquare className="h-3 w-3" /> Log Contact
                            </Button>
                        </div>
                    </>}

                    {/* ── ACTIVITY TAB ─── */}
                    {activeTab === 'activity' && <>
                        <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs w-full" onClick={() => setShowLog(true)}>
                            <Plus className="h-3 w-3" /> Add Entry
                        </Button>
                        {!allLogs.length && <p className="text-xs text-muted-foreground py-4 text-center">No activity logged yet.</p>}
                        <div className="relative space-y-3 pl-5 before:absolute before:left-[7px] before:top-1 before:h-[calc(100%-8px)] before:w-px before:bg-border">
                            {allLogs.map((log, i) => {
                                const Icon        = LOG_TYPE_ICONS[log.type] || MessageSquare;
                                const logKey      = log._id || i;
                                const isExpanded  = expandedLogs.has(logKey);
                                const isBounce    = log.deliveryStatus === 'bounced_hard' || log.deliveryStatus === 'bounced_soft';
                                const BODY_CUTOFF = 220; // chars before "Read more"
                                const bodyTrimmed = log.body && !isExpanded && log.body.length > BODY_CUTOFF
                                    ? log.body.slice(0, BODY_CUTOFF) + '…'
                                    : log.body;

                                return (
                                    <div key={logKey} className="relative">
                                        <div className="absolute -left-[13px] top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-border bg-background">
                                            <Icon className="h-2.5 w-2.5 text-muted-foreground" />
                                        </div>
                                        <div className={cn('rounded-lg border bg-muted/20 p-3 space-y-1.5',
                                            log.deliveryStatus === 'bounced_hard' ? 'border-red-500/40 bg-red-500/5' :
                                            log.deliveryStatus === 'bounced_soft' ? 'border-amber-500/30 bg-amber-500/5' :
                                            log.deliveryStatus === 'complained'   ? 'border-red-500/40 bg-red-500/5' :
                                            log.direction === 'inbound'           ? 'border-emerald-500/30 bg-emerald-500/5' :
                                            'border-border'
                                        )}>
                                            {/* Header row */}
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="capitalize text-[11px] font-bold text-foreground">{log.type}</span>
                                                    {log.direction && <span className="text-[10px] text-muted-foreground">({log.direction})</span>}
                                                    {log.outcome && <span className={cn('text-[10px] font-semibold capitalize', LOG_OUTCOME_COLOR[log.outcome] || '')}>{log.outcome}</span>}
                                                    {log.type === 'email' && log.deliveryStatus && DELIVERY_STATUS_CONFIG[log.deliveryStatus]?.label && (
                                                        <span className={cn('inline-flex items-center rounded-full border px-1.5 py-0 text-[9px] font-semibold', DELIVERY_STATUS_CONFIG[log.deliveryStatus].cls)}>
                                                            {DELIVERY_STATUS_CONFIG[log.deliveryStatus].label}
                                                        </span>
                                                    )}
                                                    {log.emailFixed && (
                                                        <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0 text-[9px] font-semibold text-emerald-600">
                                                            ✓ Fixed
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                                    {new Date(log.loggedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </span>
                                            </div>

                                            {/* Subject */}
                                            {log.subject && <p className="text-xs font-semibold text-foreground">{log.subject}</p>}

                                            {/* Body + read more */}
                                            {log.body && (
                                                <div>
                                                    <p className="text-[11px] text-muted-foreground whitespace-pre-line leading-relaxed">
                                                        {bodyTrimmed}
                                                    </p>
                                                    {log.body.length > BODY_CUTOFF && (
                                                        <button type="button"
                                                            onClick={() => toggleExpand(logKey)}
                                                            className="text-[10px] font-semibold text-primary hover:underline mt-0.5">
                                                            {isExpanded ? '↑ Show less' : '↓ Read more'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Bounce reason + delivery timestamp */}
                                            {log.deliveryUpdatedAt && log.deliveryStatus !== 'pending' && log.deliveryStatus !== 'sent' && (
                                                <p className="text-[9px] text-muted-foreground/60">
                                                    Status updated {new Date(log.deliveryUpdatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                    {log.bounceReason ? ` — ${log.bounceReason}` : ''}
                                                </p>
                                            )}

                                            {/* ── Fix email inline form — shown on bounce entries ── */}
                                            {isBounce && (
                                                fixEmailLogId === logKey ? (
                                                    <div className="mt-1.5 space-y-1.5 rounded-lg border border-red-500/30 bg-background p-2.5">
                                                        <p className="text-[10px] font-semibold text-red-600">Update email address for this lead</p>
                                                        <div className="flex gap-1.5">
                                                            <Input
                                                                type="email"
                                                                placeholder="new@email.com"
                                                                value={fixEmailVal}
                                                                onChange={e => setFixEmailVal(e.target.value)}
                                                                onKeyDown={e => e.key === 'Enter' && handleFixEmail()}
                                                                className="h-7 text-xs flex-1"
                                                                autoFocus
                                                            />
                                                            <Button size="sm" className="h-7 text-xs px-2.5 shrink-0" onClick={handleFixEmail} disabled={fixEmailSaving}>
                                                                {fixEmailSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                                                            </Button>
                                                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 shrink-0"
                                                                onClick={() => { setFixEmailLogId(null); setFixEmailVal(''); }}>
                                                                <X className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button type="button"
                                                        onClick={() => { setFixEmailLogId(logKey); setFixEmailVal(lead.email || ''); }}
                                                        className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-red-600 hover:underline">
                                                        <Pencil className="h-2.5 w-2.5" /> Fix email address
                                                    </button>
                                                )
                                            )}

                                            {log.nextFollowUpDate && (
                                                <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                                    Follow-up: {new Date(log.nextFollowUpDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </p>
                                            )}
                                            {log.loggedBy && <p className="text-[10px] text-muted-foreground/60">by {log.loggedBy.firstName} {log.loggedBy.lastName}</p>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>}

                    {/* ── PIPELINE TAB ─── */}
                    {activeTab === 'pipeline' && <>
                        <p className="text-xs text-muted-foreground">Move this lead to any stage in the funnel. Current: <strong>{STATUS_MAP[lead.status]?.label || lead.status}</strong></p>
                        <div className="space-y-1.5">
                            {PIPELINE.map(stage => {
                              // A stage is "active" if the lead's status matches the canonical key OR a legacy alias
                              const isActive = lead.status === stage.key || STATUS_ALIAS[lead.status] === stage.key;
                              return (
                                <button key={stage.key} type="button" disabled={updatingStatus || isActive}
                                    onClick={() => handleStatusChange(stage.key)}
                                    className={cn('flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 transition-all text-left',
                                        isActive ? 'border-primary/50 bg-muted' : 'border-border hover:border-primary/40 hover:bg-muted/50'
                                    )}>
                                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
                                    <span className="text-sm font-medium flex-1">{stage.label}</span>
                                    {isActive && <Check className="h-4 w-4 text-primary shrink-0" />}
                                </button>
                              );
                            })}
                        </div>
                        <Separator />
                        <div className="space-y-1.5">
                            {Object.entries(DEAD_STAGES).map(([key, { label, color }]) => {
                                const isActive = lead.status === key || STATUS_ALIAS[lead.status] === key;
                                return (
                                    <button key={key} type="button" disabled={updatingStatus || isActive}
                                        onClick={() => handleStatusChange(key)}
                                        className={cn('flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 transition-all text-left',
                                            isActive ? 'border-border/80 bg-muted' : 'border-border hover:border-border/80 hover:bg-muted/50 opacity-60 hover:opacity-80'
                                        )}>
                                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                                        <span className="text-sm font-medium flex-1" style={{ color }}>{label}</span>
                                        {isActive && <Check className="h-4 w-4 shrink-0" style={{ color }} />}
                                    </button>
                                );
                            })}
                        </div>

                        <Separator />

                        {/* Drip Sequence Toggle */}
                        <div className={cn('rounded-lg border p-3 space-y-2', lead.sequence?.active ? 'border-violet-500/30 bg-violet-500/5' : 'border-border bg-muted/20')}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Automated Drip Sequence</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {lead.sequence?.active
                                            ? `Active — Step ${(lead.sequence.step || 0) + 1}/4${lead.sequence.nextSendAt ? ` · Next: ${new Date(lead.sequence.nextSendAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}`
                                            : lead.sequence?.stoppedReason
                                                ? `Stopped (${lead.sequence.stoppedReason})`
                                                : 'Not started — activates a 4-email sequence automatically.'}
                                    </p>
                                </div>
                                {lead.unsubscribed ? (
                                    <span className="text-[11px] font-semibold text-red-500">Unsubscribed</span>
                                ) : !lead.email ? (
                                    <span className="text-[11px] text-muted-foreground">No email</span>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant={lead.sequence?.active ? 'destructive' : 'default'}
                                        className="gap-1.5 h-8 text-xs shrink-0"
                                        onClick={handleToggleSequence}
                                        disabled={togglingSeq || !['new','researching','contacted'].includes(lead.status)}
                                    >
                                        {togglingSeq
                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                            : lead.sequence?.active
                                                ? <><StopCircle className="h-3 w-3" /> Stop</>
                                                : <><Play className="h-3 w-3" /> Start</>
                                        }
                                    </Button>
                                )}
                            </div>
                            {!['new','researching','contacted'].includes(lead.status) && !lead.sequence?.active && (
                                <p className="text-[11px] text-amber-600 dark:text-amber-400">Sequence is only available for New / Researching / Contacted leads.</p>
                            )}
                            {/* ── Full drip schedule timeline ── */}
                            <div className="mt-2 space-y-1.5">
                                {(['cold_outreach','followup_1','followup_2','followup_3']).map((type, i) => {
                                    const labels   = ['Cold Outreach', 'Follow-Up 1', 'Follow-Up 2', 'Follow-Up 3'];
                                    const step     = lead.sequence?.step || 0;
                                    const sched    = (lead.sequence?.schedule || []).find(s => s.step === i);
                                    const sentAt   = sched?.sentAt;
                                    const schedAt  = sched?.scheduledAt;
                                    const isDone    = sentAt || (i < step && !lead.sequence?.active && lead.sequence?.stoppedReason === 'completed');
                                    const isCurrent = lead.sequence?.active && i === step;
                                    const isUpcoming = lead.sequence?.active && i > step;
                                    const today     = new Date(); today.setHours(0,0,0,0);
                                    const isOverdue = isCurrent && schedAt && new Date(schedAt) < today;
                                    const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
                                    return (
                                        <div key={i} className={cn('flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px]',
                                            isCurrent  ? 'bg-violet-500/10 border border-violet-500/20' :
                                            isDone     ? 'bg-emerald-500/8 border border-emerald-500/15 opacity-70' :
                                            isUpcoming ? 'bg-muted/40 border border-border/50' :
                                                         'bg-muted/20 border border-border/30 opacity-40')}>
                                            <span className="text-base leading-none shrink-0">
                                                {isDone ? '✅' : isCurrent ? '▶' : isUpcoming ? '○' : '○'}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <span className={cn('font-semibold', isCurrent ? 'text-violet-700 dark:text-violet-300' : isDone ? 'text-emerald-700 dark:text-emerald-400' : 'text-foreground')}>
                                                    {labels[i]}
                                                </span>
                                                {isCurrent && !isDone && (
                                                    <span className={cn('ml-1.5 text-[9px] font-bold uppercase rounded px-1 py-0.5', isOverdue ? 'bg-red-500/15 text-red-600' : 'bg-violet-500/15 text-violet-600')}>
                                                        {isOverdue ? '⚠ Overdue' : '● Next up'}
                                                    </span>
                                                )}
                                            </div>
                                            <span className={cn('text-[10px] shrink-0 font-medium tabular-nums',
                                                isDone ? 'text-emerald-600' : isCurrent ? (isOverdue ? 'text-red-500' : 'text-violet-600') : 'text-muted-foreground')}>
                                                {isDone ? `Sent ${fmtDate(sentAt)}` : schedAt ? fmtDate(schedAt) : '—'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>}
                </div>
            </div>

            {showEdit      && <EditLeadModal     lead={lead} onClose={() => setShowEdit(false)}     onSaved={d => { setLead(l => ({ ...l, ...d })); onRefresh?.(); }} />}
            {showOutreach  && <OutreachModal     lead={lead} onClose={() => setShowOutreach(false)} onSent={reload} />}
            {showDemo      && <SendDemoEmailModal lead={lead} onClose={() => setShowDemo(false)}     onSent={reload} />}
            {showLog       && <LogContactModal   lead={lead} onClose={() => setShowLog(false)} prefillType={logPrefill} onSaved={d => { setLead(d); onRefresh?.(); }} />}
            {showDripPicker && (
                <StartDripModal
                    lead={lead}
                    onClose={() => setShowDripPicker(false)}
                    onStarted={d => { setLead(d); onRefresh?.(); }}
                />
            )}
        </div>
    );
}

// ─── Start Drip Sequence Picker Modal ────────────────────────────────────────
function StartDripModal({ lead, onClose, onStarted }) {
    const [sequences, setSequences] = useState([]);
    const [selected, setSelected]   = useState('');
    const [loading, setLoading]     = useState(true);
    const [starting, setStarting]   = useState(false);

    useEffect(() => {
        apiClient.getProspectDripSequences().then(r => {
            const seqs = r.data || [];
            setSequences(seqs);
            const def = seqs.find(s => s.isDefault) || seqs[0];
            if (def) setSelected(def._id);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const handleStart = async () => {
        setStarting(true);
        try {
            const r = await apiClient.startProspectSequence(lead._id, selected || undefined);
            toast.success(r.message || 'Drip started!');
            onStarted(r.data);
            onClose();
        } catch (e) { toast.error(e.message || 'Failed to start drip.'); }
        finally { setStarting(false); }
    };

    const selSeq = sequences.find(s => s._id === selected);

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Play className="h-4 w-4 text-primary" /> Start Drip Sequence
                    </DialogTitle>
                    <DialogDescription>
                        Starting drip for <strong>{lead.name}</strong> ({lead.email})
                    </DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : sequences.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        No drip sequences found. Create one in the Email Templates page.
                    </div>
                ) : (
                    <div className="space-y-3 py-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Select Sequence</Label>
                        <div className="space-y-2">
                            {sequences.map(seq => (
                                <button key={seq._id} type="button"
                                    onClick={() => setSelected(seq._id)}
                                    className={cn('w-full text-left rounded-lg border px-3.5 py-3 transition-all',
                                        selected === seq._id ? 'border-primary bg-primary/5' : 'border-border hover:border-border/80 hover:bg-muted/30')}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <span className={cn('h-3.5 w-3.5 rounded-full border-2 shrink-0', selected === seq._id ? 'border-primary bg-primary' : 'border-muted-foreground/40')} />
                                            <span className="font-semibold text-sm">{seq.name}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {seq.isDefault && <span className="text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 rounded px-1.5 py-0.5">Default</span>}
                                            <span className="text-[10px] text-muted-foreground">{seq.steps?.length || 0} steps</span>
                                        </div>
                                    </div>
                                    {seq.description && <p className="text-[11px] text-muted-foreground mt-1 ml-5">{seq.description}</p>}
                                    {selected === seq._id && seq.steps?.length > 0 && (
                                        <div className="mt-2 ml-5 flex items-center gap-1 flex-wrap">
                                            {[...seq.steps].sort((a, b) => a.order - b.order).map((step, i) => (
                                                <span key={i} className="inline-flex items-center gap-1 text-[9px] text-muted-foreground">
                                                    {i > 0 && <span className="text-muted-foreground/40">→</span>}
                                                    <span className="bg-muted rounded px-1.5 py-0.5 font-medium">{step.templateType}</span>
                                                    {step.delayDays > 0 && <span className="text-muted-foreground/60">+{step.delayDays}d</span>}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                        {sequences.length === 0 && <p className="text-xs text-amber-600">No drip sequences configured. Using built-in default steps.</p>}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleStart} disabled={starting || loading} className="gap-1.5">
                        {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        Start Drip
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ open, title, description, confirmLabel = 'Confirm', confirmCls = '', icon: Icon, onConfirm, onClose }) {
    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {Icon && <Icon className="h-4 w-4 text-primary" />}
                        {title}
                    </DialogTitle>
                    {description && <DialogDescription className="text-sm text-muted-foreground pt-1">{description}</DialogDescription>}
                </DialogHeader>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                    <Button size="sm" className={cn('gap-1.5', confirmCls)} onClick={() => { onConfirm(); onClose(); }}>
                        {confirmLabel}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SuperAdminProspectsPage() {
    const [leads, setLeads]           = useState([]);
    const [stats, setStats]           = useState(null);
    const [loading, setLoading]       = useState(true);
    const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

    const [search, setSearch]               = useState('');
    const [stageFilter, setStageFilter]     = useState('');
    const [tierFilter, setTierFilter]       = useState('');
    const [industryFilter, setIndustryFilter] = useState('');
    const [cityFilter, setCityFilter]       = useState('');
    const [sizeFilter, setSizeFilter]       = useState('');
    const [fundingFilter, setFundingFilter] = useState('');
    const [showFilters, setShowFilters]     = useState(false);
    const [dripFilter, setDripFilter]       = useState(''); // '' | 'active' | 'inactive'
    const [bouncedFilter, setBouncedFilter] = useState(false);
    const [followupFilter, setFollowupFilter] = useState(''); // '' | 'today' | 'this_week' | 'overdue'
    const [view, setView]                   = useState('table'); // 'table' | 'kanban'

    const [selectedLead, setSelectedLead]     = useState(null);
    const [showBulkInsert, setShowBulkInsert] = useState(false);
    const [deletingId, setDeletingId]         = useState(null);
    const [selectedIds, setSelectedIds]       = useState(new Set());
    const [showBulkEmail, setShowBulkEmail]   = useState(false);
    const [bulkSeqRunning, setBulkSeqRunning] = useState(false);
    const [verifyRunning, setVerifyRunning]   = useState(false);
    const [confirmDialog, setConfirmDialog]   = useState(null); // { title, description, confirmLabel, confirmCls, icon, onConfirm }

    const searchTimer = useRef(null);

    const loadLeads = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            // Kanban needs more cards visible at once — use a larger page size
            const limit = view === 'kanban' ? 100 : 25;
            const params = { page, limit };
            if (search)         params.search       = search;
            if (stageFilter)    params.status       = stageFilter;
            if (tierFilter)     params.tier         = tierFilter;
            if (industryFilter) params.industry     = industryFilter;
            if (cityFilter)     params.city         = cityFilter;
            if (sizeFilter)     params.size         = sizeFilter;
            if (fundingFilter)  params.fundingStage = fundingFilter;
            if (dripFilter)      params.drip     = dripFilter;
            if (bouncedFilter)   params.bounced  = 'true';
            if (followupFilter)  params.followup = followupFilter;
            const r = await apiClient.getProspectLeads(params);
            setLeads(r.data || []);
            setPagination(r.pagination || { total: 0, page: 1, totalPages: 1 });
        } catch (e) { toast.error(e.message || 'Failed to load prospects.'); }
        finally { setLoading(false); }
    }, [search, stageFilter, tierFilter, industryFilter, cityFilter, sizeFilter, fundingFilter, dripFilter, bouncedFilter, followupFilter, view]);

    const loadStats = useCallback(async () => {
        try { const r = await apiClient.getProspectLeadStats(); setStats(r.data); } catch { /* silent */ }
    }, []);

    // Immediate reload on dropdown / chip filters or view mode change
    useEffect(() => { loadLeads(1); loadStats(); }, [stageFilter, tierFilter, sizeFilter, fundingFilter, dripFilter, bouncedFilter, followupFilter, view]);
    // Debounced reload on text inputs (search, industry, city)
    useEffect(() => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => loadLeads(1), 350);
        return () => clearTimeout(searchTimer.current);
    }, [search, industryFilter, cityFilter]);

    const refresh = () => { loadLeads(pagination.page); loadStats(); };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        setConfirmDialog({
            title: 'Delete Prospect?',
            description: 'This will permanently remove the lead and all its activity logs. This cannot be undone.',
            confirmLabel: 'Delete',
            confirmCls: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            icon: Trash2,
            onConfirm: async () => {
                setDeletingId(id);
                try { await apiClient.deleteProspectLead(id); toast.success('Deleted.'); refresh(); }
                catch (e) { toast.error(e.message || 'Delete failed.'); }
                finally { setDeletingId(null); }
            },
        });
    };

    const toggleSelect    = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleSelectAll = () => setSelectedIds(prev => prev.size === leads.length ? new Set() : new Set(leads.map(l => l._id)));
    const allSelected     = leads.length > 0 && selectedIds.size === leads.length;
    const someSelected    = selectedIds.size > 0 && !allSelected;

    const overdueCount = leads.filter(l => l.nextAction?.date && new Date(l.nextAction.date) < new Date() && !['converted','not_interested','lost'].includes(l.status)).length;

    const handleBulkStartSequence = () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setConfirmDialog({
            title: `Start Drip for ${ids.length} lead(s)?`,
            description: 'Only leads with a valid email in New / Researching / Contacted stage will be started. Others are skipped.',
            confirmLabel: 'Start Drip',
            confirmCls: 'border-blue-500 bg-blue-600 text-white hover:bg-blue-700',
            icon: Play,
            onConfirm: async () => {
                setBulkSeqRunning(true);
                let ok = 0, skip = 0;
                for (const id of ids) {
                    try { await apiClient.startProspectSequence(id); ok++; }
                    catch { skip++; }
                }
                setBulkSeqRunning(false);
                setSelectedIds(new Set());
                toast.success(`Drip started for ${ok} lead(s).${skip > 0 ? ` Skipped ${skip} (no email or wrong stage).` : ''}`);
                refresh();
            },
        });
    };
    const handleBulkVerifyEmails = () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setConfirmDialog({
            title: `Verify ${ids.length} Email${ids.length !== 1 ? 's' : ''}?`,
            description: `Uses Hunter.io (if configured) or free DNS MX check. Valid leads in "New" stage are moved to "Researching" automatically.`,
            confirmLabel: 'Verify Emails',
            confirmCls: 'bg-violet-600 text-white hover:bg-violet-700',
            icon: ShieldCheck,
            onConfirm: async () => {
                setVerifyRunning(true);
                try {
                    const r = await apiClient.verifyProspectEmails(ids);
                    const { summary } = r;
                    const parts = [];
                    if (summary.advanced)  parts.push(`${summary.advanced} moved to Researching`);
                    if (summary.verified - summary.advanced > 0) parts.push(`${summary.verified - summary.advanced} valid (already past New)`);
                    if (summary.failed)    parts.push(`${summary.failed} invalid domains`);
                    if (summary.unknown)   parts.push(`${summary.unknown} unconfirmed`);
                    toast.success(`Verification done — ${parts.join(' · ')}`);
                    setSelectedIds(new Set());
                    refresh();
                } catch (e) { toast.error(e.message || 'Verification failed.'); }
                finally { setVerifyRunning(false); }
            },
        });
    };

    const activeFilterCount = [industryFilter, cityFilter, sizeFilter, fundingFilter, dripFilter, bouncedFilter ? 'bounced' : '', followupFilter].filter(Boolean).length;
    const clearAllFilters   = () => { setStageFilter(''); setIndustryFilter(''); setCityFilter(''); setSizeFilter(''); setFundingFilter(''); setDripFilter(''); setBouncedFilter(false); setFollowupFilter(''); };

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                        <Crosshair className="h-5 w-5 text-primary" /> Prospect Leads
                    </h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Outbound B2B pipeline — track every touchpoint from scrape to conversion.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={refresh} className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
                    <Button size="sm" onClick={() => setShowBulkInsert(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Add / Import</Button>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                    {[
                        { label: 'Total Prospects', value: stats.total, icon: Crosshair, cls: '' },
                        { label: 'Pipeline Value', value: stats.pipelineValue > 0 ? `₹${(stats.pipelineValue).toLocaleString('en-IN')}` : '—', icon: IndianRupee, cls: 'text-emerald-600 dark:text-emerald-400' },
                        { label: 'Converted', value: stats.byStatus.converted || 0, icon: TrendingUp, cls: 'text-green-600 dark:text-green-400' },
                        { label: 'Conv. Rate', value: stats.conversionRate !== null ? `${stats.conversionRate}%` : '—', icon: TrendingUp, cls: 'text-blue-600 dark:text-blue-400' },
                        { label: 'Overdue Actions', value: stats.overdueActions || 0, icon: AlertTriangle, cls: stats.overdueActions > 0 ? 'text-red-500' : '' },
                    ].map(({ label, value, icon: Icon, cls }) => (
                        <Card key={label} className="py-0">
                            <CardContent className="p-3">
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
                                    <Icon className="h-3.5 w-3.5 text-muted-foreground/40" />
                                </div>
                                <p className={cn('text-2xl font-black tabular-nums', cls || 'text-foreground')}>{value}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Pipeline Funnel */}
            {stats && (
                <Card className="py-0">
                    <CardContent className="p-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Pipeline Funnel — click a stage to filter</p>
                        <PipelineFunnel stats={stats} activeStage={stageFilter}
                            onStageClick={s => setStageFilter(prev => prev === s ? '' : s)} />
                    </CardContent>
                </Card>
            )}

            {/* Toolbar */}
            <div className="space-y-2">
                {/* Row 1: search + tier + filters button + view toggle */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Search */}
                    <div className="relative min-w-[200px] flex-1 max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, domain, contact…" className="pl-9 text-sm h-9" />
                    </div>

                    {/* Tier pills */}
                    <div className="flex gap-1 shrink-0">
                        {['', '1', '2', '3', '4'].map(t => (
                            <button key={t} type="button" onClick={() => setTierFilter(t)}
                                className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors',
                                    tierFilter === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-primary')}>
                                {t ? `T${t}` : 'All'}
                            </button>
                        ))}
                    </div>

                    {/* Drip sequence filter */}
                    <select
                        value={dripFilter}
                        onChange={e => setDripFilter(e.target.value)}
                        className={cn(
                            'h-7 rounded-full border px-2.5 text-[11px] font-semibold transition-colors shrink-0 bg-background cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring',
                            dripFilter
                                ? 'border-blue-500 text-blue-600 bg-blue-500/5'
                                : 'border-border text-muted-foreground hover:border-primary hover:text-primary',
                        )}
                    >
                        <option value="">Drip: All</option>
                        <option value="active">Drip: Ongoing</option>
                        <option value="completed">Drip: Completed</option>
                        <option value="stopped">Drip: Stopped</option>
                        <option value="not_started">Drip: Not Started</option>
                    </select>

                    {/* Bounced filter pill */}
                    <button type="button"
                        onClick={() => setBouncedFilter(p => !p)}
                        className={cn('flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors shrink-0',
                            bouncedFilter
                                ? 'bg-red-500 text-white border-red-500'
                                : 'border-red-500/40 text-red-600 hover:bg-red-500/10')}>
                        ⛔ Bounced
                    </button>

                    {/* Follow-up date filter pills */}
                    {[
                        { key: 'overdue',   label: '⚠ Overdue',    active: 'bg-amber-500 text-white border-amber-500', inactive: 'border-amber-500/40 text-amber-600 hover:bg-amber-500/10' },
                        { key: 'today',     label: '📅 Due Today',  active: 'bg-emerald-600 text-white border-emerald-600', inactive: 'border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10' },
                        { key: 'this_week', label: '🗓 This Week',  active: 'bg-violet-600 text-white border-violet-600', inactive: 'border-violet-500/40 text-violet-600 hover:bg-violet-500/10' },
                    ].map(({ key, label, active, inactive }) => (
                        <button key={key} type="button"
                            onClick={() => setFollowupFilter(p => p === key ? '' : key)}
                            className={cn('flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors shrink-0',
                                followupFilter === key ? active : inactive)}>
                            {label}
                        </button>
                    ))}

                    {/* Filters toggle */}
                    <Button
                        type="button"
                        variant={showFilters || activeFilterCount > 0 ? 'default' : 'outline'}
                        size="sm"
                        className="h-9 gap-1.5 shrink-0"
                        onClick={() => setShowFilters(p => !p)}
                    >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-foreground text-primary text-[10px] font-bold leading-none">
                                {activeFilterCount}
                            </span>
                        )}
                    </Button>

                    {/* View toggle */}
                    <div className="flex rounded-md border border-border overflow-hidden shrink-0 ml-auto">
                        {[{ v: 'table', icon: List }, { v: 'kanban', icon: LayoutGrid }].map(({ v, icon: Icon }) => (
                            <button key={v} type="button" onClick={() => setView(v)}
                                className={cn('flex items-center justify-center h-9 w-9 transition-colors', view === v ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50')}>
                                <Icon className="h-4 w-4" />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Expanded filter panel */}
                {showFilters && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-lg border border-border bg-muted/30 p-3">
                        {/* Industry */}
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <Briefcase className="h-3 w-3" />Industry
                            </label>
                            <div className="relative">
                                <Input
                                    value={industryFilter}
                                    onChange={e => setIndustryFilter(e.target.value)}
                                    placeholder="SaaS, Fintech…"
                                    className="h-8 text-xs"
                                />
                            </div>
                        </div>

                        {/* City / Location */}
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />City / Location
                            </label>
                            <Input
                                value={cityFilter}
                                onChange={e => setCityFilter(e.target.value)}
                                placeholder="Mumbai, Bangalore…"
                                className="h-8 text-xs"
                            />
                        </div>

                        {/* Company Size */}
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <Building2 className="h-3 w-3" />Company Size
                            </label>
                            <select
                                value={sizeFilter}
                                onChange={e => setSizeFilter(e.target.value)}
                                className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                <option value="">All sizes</option>
                                {['1-10','11-20','21-50','51-100','101-200','201-500','501-1000','1000+'].map(s => (
                                    <option key={s} value={s}>{s} employees</option>
                                ))}
                            </select>
                        </div>

                        {/* Funding Stage */}
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <Banknote className="h-3 w-3" />Funding Stage
                            </label>
                            <select
                                value={fundingFilter}
                                onChange={e => setFundingFilter(e.target.value)}
                                className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                <option value="">All stages</option>
                                {['Bootstrapped','Pre-Seed','Seed','Series A','Series B','Series C','Series D+','Public','Unknown'].map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Active filter chips */}
                {(stageFilter || activeFilterCount > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {stageFilter && (
                            <button type="button" onClick={() => setStageFilter('')}
                                className="flex items-center gap-1 rounded-full border border-primary/50 bg-primary/10 text-primary px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-primary/20">
                                Stage: {STATUS_MAP[stageFilter]?.label} <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {dripFilter && (
                            <button type="button" onClick={() => setDripFilter('')}
                                className="flex items-center gap-1 rounded-full border border-blue-500/50 bg-blue-500/10 text-blue-700 dark:text-blue-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-blue-500/20">
                                <span className={cn('h-1.5 w-1.5 rounded-full bg-blue-500', dripFilter === 'active' && 'animate-pulse')} />
                                Drip: {{ active: 'Ongoing', completed: 'Completed', stopped: 'Stopped', not_started: 'Not Started' }[dripFilter] || dripFilter}
                                <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {bouncedFilter && (
                            <button type="button" onClick={() => setBouncedFilter(false)}
                                className="flex items-center gap-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-red-500/20">
                                ⛔ Bounced emails <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {followupFilter && (
                            <button type="button" onClick={() => setFollowupFilter('')}
                                className="flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-amber-500/20">
                                📅 Follow-up: {followupFilter === 'today' ? 'Due Today' : followupFilter === 'this_week' ? 'This Week' : 'Overdue'} <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {industryFilter && (
                            <button type="button" onClick={() => setIndustryFilter('')}
                                className="flex items-center gap-1 rounded-full border border-violet-500/50 bg-violet-500/10 text-violet-700 dark:text-violet-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-violet-500/20">
                                <Briefcase className="h-2.5 w-2.5" />{industryFilter} <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {cityFilter && (
                            <button type="button" onClick={() => setCityFilter('')}
                                className="flex items-center gap-1 rounded-full border border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-sky-500/20">
                                <MapPin className="h-2.5 w-2.5" />{cityFilter} <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {sizeFilter && (
                            <button type="button" onClick={() => setSizeFilter('')}
                                className="flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-amber-500/20">
                                <Building2 className="h-2.5 w-2.5" />{sizeFilter} <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        {fundingFilter && (
                            <button type="button" onClick={() => setFundingFilter('')}
                                className="flex items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-emerald-500/20">
                                <Banknote className="h-2.5 w-2.5" />{fundingFilter} <X className="h-2.5 w-2.5" />
                            </button>
                        )}
                        <button type="button" onClick={clearAllFilters}
                            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors">
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex h-48 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : view === 'kanban' ? (
                <>
                    <KanbanBoard leads={leads} onSelect={setSelectedLead} />
                    <ProspectPagination
                        page={pagination.page}
                        totalPages={pagination.totalPages}
                        total={pagination.total}
                        onPageChange={p => loadLeads(p)}
                    />
                </>
            ) : (
                <Card>
                    <CardContent className="p-0">
                        {leads.length === 0 ? (
                            <div className="flex h-48 flex-col items-center justify-center gap-3">
                                <Crosshair className="h-8 w-8 text-muted-foreground/30" />
                                <p className="text-sm text-muted-foreground">No prospects found.</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10 pr-0">
                                            <button type="button" onClick={toggleSelectAll} className="flex items-center justify-center h-4 w-4 text-muted-foreground hover:text-foreground">
                                                {allSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : someSelected ? <CheckSquare className="h-4 w-4 text-primary/50" /> : <Square className="h-4 w-4" />}
                                            </button>
                                        </TableHead>
                                        <TableHead>Company</TableHead>
                                        <TableHead className="hidden md:table-cell">Contact</TableHead>
                                        <TableHead className="hidden sm:table-cell">Industry</TableHead>
                                        <TableHead>Tier</TableHead>
                                        <TableHead>Stage</TableHead>
                                        <TableHead className="hidden lg:table-cell">Next Action</TableHead>
                                        <TableHead className="hidden lg:table-cell">Logs</TableHead>
                                        <TableHead className="w-[70px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {leads.map(lead => {
                                        const overdue = lead.nextAction?.date && new Date(lead.nextAction.date) < new Date();
                                        return (
                                            <TableRow key={lead._id} className={cn('cursor-pointer hover:bg-muted/50', selectedIds.has(lead._id) && 'bg-primary/5')} onClick={() => setSelectedLead(lead)}>
                                                <TableCell className="pr-0" onClick={e => { e.stopPropagation(); toggleSelect(lead._id); }}>
                                                    <div className="flex items-center justify-center h-4 w-4 text-muted-foreground">
                                                        {selectedIds.has(lead._id) ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="font-semibold text-foreground">{lead.name}</div>
                                                    <a href={`https://${lead.domain}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                                        className="flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-primary">
                                                        {lead.domain} <ExternalLink className="h-2.5 w-2.5" />
                                                    </a>
                                                    {lead.dealValue > 0 && <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">₹{lead.dealValue.toLocaleString('en-IN')}/mo</span>}
                                                    {lead.sequence?.active && (() => {
                                                        const stepLabels = ['Cold Outreach','* Follow-Up 1','* Follow-Up 2','* Follow-Up 3'];
                                                        const s = lead.sequence;
                                                        const today = new Date(); today.setHours(0,0,0,0);
                                                        const isOverdue = s.nextSendAt && new Date(s.nextSendAt) < today;
                                                        const label = stepLabels[s.step] || `Step ${s.step + 1}`;
                                                        const date  = s.nextSendAt ? new Date(s.nextSendAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
                                                        return (
                                                            <span className={cn('mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold',
                                                                isOverdue ? 'bg-red-500/10 text-red-600' : 'bg-violet-500/10 text-violet-600')}>
                                                                {isOverdue ? '⚠' : '▶'} {label}{date ? ` · ${date}` : ''}
                                                            </span>
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    {lead.contactPerson
                                                        ? <div><div className="text-sm text-foreground">{lead.contactPerson}</div><div className="text-[11px] text-muted-foreground">{lead.contactPersonRole || lead.email}</div></div>
                                                        : <span className="text-[11px] text-muted-foreground">{lead.email || '—'}</span>}
                                                </TableCell>
                                                <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{lead.industry || '—'}</TableCell>
                                                <TableCell><TierStars tier={lead.tier} /></TableCell>
                                                <TableCell><StatusBadge status={lead.status} /></TableCell>
                                                <TableCell className="hidden lg:table-cell min-w-[140px]">
                                                    {(() => {
                                                        const na = lead.nextAction;
                                                        const seq = lead.sequence;
                                                        // Action type → icon
                                                        const typeIcon = { email: Mail, call: Phone, linkedin: ExternalLink, demo: Calendar, meeting: Users, proposal: ArrowRight, none: Check };
                                                        // Relative date label
                                                        const relDate = (d) => {
                                                            const diff = Math.round((new Date(d).setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / 86400000);
                                                            if (diff < -1) return { label: `${Math.abs(diff)}d overdue`, cls: 'text-red-500' };
                                                            if (diff === -1) return { label: 'Yesterday', cls: 'text-red-400' };
                                                            if (diff === 0)  return { label: 'Today',    cls: 'text-emerald-600 font-bold' };
                                                            if (diff === 1)  return { label: 'Tomorrow', cls: 'text-amber-600' };
                                                            if (diff <= 7)   return { label: `In ${diff}d`, cls: 'text-blue-600' };
                                                            return { label: new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }), cls: 'text-muted-foreground' };
                                                        };

                                                        if (na?.date) {
                                                            const { label, cls } = relDate(na.date);
                                                            const ActionIcon = typeIcon[na.type] || Mail;
                                                            return (
                                                                <div className="space-y-0.5">
                                                                    <div className={cn('flex items-center gap-1 text-[11px] font-semibold', overdue ? 'text-red-500' : cls)}>
                                                                        {overdue ? <AlertTriangle className="h-3 w-3 shrink-0" /> : <ActionIcon className="h-3 w-3 shrink-0" />}
                                                                        <span className="capitalize">{na.type}</span>
                                                                        <span className={cn('ml-0.5', overdue ? 'text-red-500' : cls)}>· {label}</span>
                                                                    </div>
                                                                    {na.notes && <p className="text-[10px] text-muted-foreground truncate max-w-[160px]">{na.notes}</p>}
                                                                </div>
                                                            );
                                                        }
                                                        // No manual action — show drip next step if active
                                                        if (seq?.active && seq.nextSendAt) {
                                                            const stepLabels = ['Cold Email','Follow-Up 1','Follow-Up 2','Follow-Up 3'];
                                                            const { label, cls } = relDate(seq.nextSendAt);
                                                            const today = new Date(); today.setHours(0,0,0,0);
                                                            const isOv  = new Date(seq.nextSendAt) < today;
                                                            return (
                                                                <div className={cn('flex items-center gap-1 text-[11px] font-semibold', isOv ? 'text-red-500' : 'text-violet-600')}>
                                                                    {isOv ? <AlertTriangle className="h-3 w-3 shrink-0" /> : <Mail className="h-3 w-3 shrink-0" />}
                                                                    <span>{stepLabels[seq.step] || 'Drip'}</span>
                                                                    <span className={cn('ml-0.5', cls)}>· {label}</span>
                                                                </div>
                                                            );
                                                        }
                                                        return <span className="text-[11px] text-muted-foreground/40">—</span>;
                                                    })()}
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    <span className="text-sm tabular-nums font-medium">{(lead.contactLogs?.length || 0) + (lead.outreachHistory?.length || 0) + (lead.followUps?.length || 0)}</span>
                                                </TableCell>
                                                <TableCell onClick={e => e.stopPropagation()}>
                                                    <button type="button" onClick={e => handleDelete(lead._id, e)} disabled={deletingId === lead._id}
                                                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                                                        {deletingId === lead._id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                        <ProspectPagination
                            page={pagination.page}
                            totalPages={pagination.totalPages}
                            total={pagination.total}
                            onPageChange={p => loadLeads(p)}
                        />
                    </CardContent>
                </Card>
            )}

            {/* ── Floating bulk action bar ── */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-xl border border-border bg-background shadow-2xl px-4 py-2.5">
                    <span className="text-sm font-bold text-foreground mr-1">{selectedIds.size} selected</span>
                    <div className="h-4 w-px bg-border" />
                    <Button size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setShowBulkEmail(true)}>
                        <Send className="h-3.5 w-3.5" /> Bulk Email
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs border-blue-500/40 text-blue-600 hover:bg-blue-500/10"
                        onClick={handleBulkStartSequence} disabled={bulkSeqRunning}>
                        {bulkSeqRunning
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Play className="h-3.5 w-3.5" />}
                        Start Drip
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs border-violet-500/40 text-violet-600 hover:bg-violet-500/10"
                        onClick={handleBulkVerifyEmails} disabled={verifyRunning}>
                        {verifyRunning
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <ShieldCheck className="h-3.5 w-3.5" />}
                        Verify Emails
                    </Button>
                    <div className="h-4 w-px bg-border" />
                    <Button size="sm" variant="ghost" className="gap-1.5 h-8 text-xs text-muted-foreground" onClick={() => setSelectedIds(new Set())}>
                        <X className="h-3.5 w-3.5" /> Clear
                    </Button>
                </div>
            )}

            {/* Detail drawer */}
            {selectedLead && <LeadDetail lead={selectedLead} onClose={() => setSelectedLead(null)} onRefresh={refresh} />}
            {showBulkInsert && <BulkInsertModal onClose={() => setShowBulkInsert(false)} onInserted={refresh} />}
            {confirmDialog && (
                <ConfirmDialog
                    open={!!confirmDialog}
                    title={confirmDialog.title}
                    description={confirmDialog.description}
                    confirmLabel={confirmDialog.confirmLabel}
                    confirmCls={confirmDialog.confirmCls}
                    icon={confirmDialog.icon}
                    onConfirm={confirmDialog.onConfirm}
                    onClose={() => setConfirmDialog(null)}
                />
            )}
            {showBulkEmail && (
                <BulkEmailModal
                    leadIds={[...selectedIds]}
                    onClose={() => setShowBulkEmail(false)}
                    onDone={() => { setSelectedIds(new Set()); setShowBulkEmail(false); refresh(); }}
                />
            )}
        </div>
    );
}
